// Note: all references to environment variables should be getters to avoid issues with patchEnv in jest tests
import {
  facebookCompetitiveMediaCardDefaults,
  facebookGalleryCardDefaults,
  facebookMediaCardDefaults,
  facebookMetricFormats,
} from '@/app/facebook/constants';
import {
  pinterestGalleryCardDefaults,
  pinterestMediaCardDefaults,
  pinterestMetricFormats,
} from '@/app/pinterest/constants';
import {
  tiktokGalleryCardDefaults,
  tiktokMediaCardDefaults,
  tiktokMetricFormats,
} from '@/app/tiktok/constants';
import {
  twitterGalleryCardDefaults,
  twitterMediaCardDefaults,
  twitterMetricFormats,
} from '@/app/twitter/constants';
import {
  youtubeGalleryCardDefaults,
  youtubeMediaCardDefaults,
  youtubeMetricFormats,
} from '@/app/youtube/constants';
import { env } from '@/env';
import { colours } from '@/ux/colours';

import { brandingDashSocial } from './branding';

export const TOKEN_KEY = 'token';

const isPreviewUrl = (url) =>
  /^.*pr-.*\.dashweb\.dhpreview\.link$/.test(url) ||
  /^.*pr-.*(-dash-web|-dashing-ui)?\.preview\.(dhdev\.co|dashsocial\.com)$/.test(url);

export function originForReport() {
  const { origin } = window.location;
  return env.reportOriginOverride || (isPreviewUrl(origin) ? origin : env.dashwebUrl);
}

export const isDatadogSyntheticsBrowser = !!window._DATADOG_SYNTHETICS_BROWSER;

export const DEFAULT_FONT_SIZE = 16;

export const BANNER_INFO_URL = 'https://cdn.dashsocial.com/banners/banner-info.json';

export const platformColors = {
  tiktok: '#F26FA4',
  instagram: '#27C449',
  instagramStories: '#E179DA',
  instagramUgc: '#8943FF',
  facebook: '#4990E2',
  linkedin: '#3F8285',
  pinterest: '#E36042',
  reddit: '#DA65E4',
  twitter: '#F5A623',
  youtube: '#39BCE6',
};

export const sentimentColors = {
  negative: colours.ERROR.ERROR_500,
  positive: colours.SUCCESS.SUCCESS_500,
  neutral: colours.ACTION.ACTION_500,
};

export const mediaTypeColors = {
  allMedia: '#4990e2',
  video: '#27C449',
  reel: '#E36042',
  photo: '#F5A623',
  carousel: '#E179DA',
  videoReel: '#8943FF',
  photoCarousel: '#39BCE6',
  link: '#C3E921',
  text: '#F0649A',
};

export const filterSizeOptions = ['Any', '800x600', '1024x768', '1600x1200'];

export const filterColorOptions = [
  { name: 'red', hex: '#D0021B' },
  { name: 'orange', hex: '#F5A623' },
  { name: 'yellow', hex: '#F8E71C' },
  { name: 'green', hex: '#30D321' },
  { name: 'cyan', hex: '#50E3C2' },
  { name: 'blue', hex: '#4A90E2' },
  { name: 'purple', hex: '#9013FE' },
  { name: 'pink', hex: '#FF9DA9' },
  { name: 'brown', hex: '#8B572A' },
  { name: 'black', hex: '#000000' },
  { name: 'gray', hex: '#9B9B9B' },
  { name: 'white', hex: '#FFFFFF' },
];

export const dateRangeOptions = [
  { label: 'All Time', value: null },
  { label: 'Last 24 Hours', value: 1 },
  { label: 'Last 3 Days', value: 3 },
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 4 Weeks', value: 28 },
];

// The max number of days prior to a brand connecting their Twitter Account that we have data available
export const maxTwitterDataCutOffDays = 90;

export const maxMentionsPerTweet = 10;

export const maxTweetThreads = 25;

const discontinuationNote =
  'This metric has been discontinued for posts published after April 15, 2025. It remains visible for historical reporting.';

const discontinuationNoteStories =
  'This data will be available 24 hours after the story was published.\n\nThis metric has been discontinued for stories published after May 15, 2025. It remains visible for historical reporting.';

const metaInstagramMediaPopup = Object.freeze({
  totalVideoViews:
    'The total number times your organic and promoted videos published during the reporting period were played for at least 1ms. This excludes replays. This may include views from IGTV.',
  amountSpentPaid: "The total amount of money you've spent promoting this post",
  clicksPaid:
    'The total number of clicks this promoted post received. This can include link clicks, clicks to your profile, and more.',
  comments: 'The total number of times people commented on this post',
  costPerThruplayPaid:
    'The average cost per ThruPlay for this video\n' +
    '\n' +
    'Cost Per ThruPlay = Amount Spent / Total ThruPlays',
  cpcPaid:
    'The average cost per click on this promoted post\n' +
    '\n' +
    'CPC = Amount Spent / Total Promoted Clicks',
  cpmPaid:
    'The average cost per 1,000 impressions on this promoted post\n' +
    '\n' +
    'CPM = Amount Spent / Total Promoted Impressions x 1,000',
  ctrPaid:
    'The percentage of times people clicked your promoted post after seeing it\n' +
    '\n' +
    'CTR = Total Clicks / Promoted Impressions',
  totalEngagements:
    'The total number of organic and promoted engagements on this post\n' +
    '\n' +
    'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)\n' +
    '\n' +
    'Note: Promoted Unsaves and Reposts are unavailable',
  totalEngagementsNewMetrics:
    'The total number of organic and promoted engagements on this post\n' +
    '\n' +
    'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)\n' +
    '\n' +
    'Note: Promoted Unsaves and Reposts are unavailable',
  engagementRate:
    'The organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Followers',
  engagementRateImpressions:
    'The organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Impresssions\n' +
    `\n${discontinuationNote}`,
  engagementRatePaid:
    'The promoted engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Promoted Engagements / Promoted Impressions',
  frequencyPaid: 'The average number of times people saw this promoted post',
  impressions: `The total number of impressions this post received\n\n${discontinuationNote}`,
  impressionsPaid: 'The total number of promoted impressions this post received',
  likes: 'The total number of likes this post received',
  reachTotal: 'The total number of unique accounts that saw this post',
  reposts: 'The number of times your post has been reposted',
  thruplaysPaid:
    'The total number of times this promoted post was played to completion or for at least 15 seconds',
  videoPlays25Paid: 'The total number of times this promoted video was played to 25% of its length',
  videoPlays50Paid: 'The total number of times this promoted video was played to 50% of its length',
  videoPlays75Paid: 'The total number of times this promoted video was played to 75% of its length',
  videoPlays100Paid: 'The total number of times this promoted video was played to 100% completion',
  videoViewsTotal: `The number of times this video was initially played for at least 1ms. This excludes replays.\n\n${discontinuationNote}`,
});

const instagramInsights = Object.freeze({
  boosted: 'This post has been promoted',
  effectivenessNonVideo:
    'The percentage of users that saw your organic post and engaged with it\n' +
    '\n' +
    'Effectiveness = (Likes + Comments + Saves) / Reach\n' +
    `\n${discontinuationNote}`,
  effectiveness:
    'The percentage of users that saw your organic post and engaged with it\n' +
    '\n' +
    'Effectiveness = (Likes + Comments + Saves + Video Views) / Reach\n' +
    `\n${discontinuationNote}`,
  effectivenessUgc:
    'The percentage of people that saw your post and engaged with it\n' +
    '\n' +
    'Effectiveness = Likes + Comments / Est. Reach',
  engagementRate:
    'The organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Followers',
  engagementRateImpressions:
    'The organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Impressions\n' +
    `\n${discontinuationNote}`,
  engagementRateUgc:
    'The percentage of your followers that engaged with your post\n' +
    '\n' +
    'Engagement Rate = Likes + Comments / Followers',
  igOther:
    "As you are not photo tagged or @ mentioned in this piece of content, Instagram's API only allows us to show you a bit of info. Click 'See on Instagram' to get more info on this post!",
  totalEngagements:
    'The number of likes, saves, comments and shares on your post minus the number of unlikes, unsaves and deleted comments',
  totalEngagementsNewMetrics:
    'The number of engagements on this post\n' +
    '\n' +
    'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)',
  totalEngagementsUGC:
    'The number of people who engaged with this post\n\nEngagements = Likes + Comments',
  paidTotalEngagements:
    'The total number of Paid Engagements on this post\n' +
    '\n' +
    'Paid Total Engagements = Paid Likes + Paid Comments',
  organicTotalEngagements:
    'The total number of Organic Engagements on this post\n' +
    '\n' +
    'Organic Total Engagements = Organic Likes + Organic Comments + Saves',
  paidImpressions: 'Total impressions from all paid placements',
  unavailablePaidMetrics: 'This boosted data is not available via the API 3 years after publishing',
  reach: 'The number of unique accounts who saw this post',
  estimatedReach: 'The estimated number of unique accounts who saw this post',
  boostedReach: 'The number of unique accounts who saw this post. Measures organic Reach only.',
  likes: 'The number of people who liked this post',
  comments: 'The number of people who commented on this post',
  entertainmentScore:
    'The Entertainment Score of this Reel on a scale of 0 to 10. Your score is based on volume of organic engagements compared to reach.',
  follows: 'The number of accounts that started following you after viewing your post',
  emv: 'The earned media value of this post.\n\nEMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  followersGained:
    'The estimated number of followers this post gained for your brand. Only available for UGC from creators in Your Creators or Relationships.',
  profileVisits: 'The number of times your profile was visited after viewing your post',
  profileClicks:
    'The number of actions people take when they visit your profile after viewing your post. This can include bio link clicks, calls, emails, and more.',
  impressions: discontinuationNote,
  videoViews: discontinuationNote,
  effectivenessEngagements: `The percentage of users that saw this post and engaged with it

Effectiveness = Engagements / Reach`,
  engagementRateViews: `The percentage of users who engaged with this posts after seeing it

Engagement Rate (Views) = Engagements / Views`,
  reposts: 'The number of times your post has been reposted',
  crosspostedViews:
    'Total views across Instagram and Facebook for content crossposted natively between platforms.',
  facebookViews:
    'The number of views this post received on Facebook, for content crossposted natively from Instagram.',
  views:
    "The number of times this post was displayed on a person's screen. This includes repeat views.",
  viewsVideo:
    'The number of times your video was played or replayed for 1 ms or more. Replays are counted after the initial play in the same video session.',
  sharesPaid:
    'Shares from Ad campaigns. May not match Instagram native or Business Manager insights for Boosted promotions due to API limitations.',
  savedPaid:
    'Saves from Ad campaigns. May not match Instagram native or Business Manager insights for Boosted promotions due to API limitations.',
});

const instagramAdAccountScopedInsightsSuffixes = Object.freeze({
  reachTotal:
    "Posts promoted from multiple ad accounts can't have reach totalled. The Meta API does not provide total reach. As a result, the sum of organic and promoted reach may be higher than what you see in Meta.",
  frequencyPaid:
    "Posts promoted from multiple ad accounts can't show a combined frequency due to potential data inaccuracies.",
});

const instagramReelsInsightsTooltips = Object.freeze({
  videoViewsOrganic: `The number of times this video was initially played for at least 1ms. This excludes replays.\n\n${discontinuationNote}`,
  videoViewsPromoted:
    'The number of times your promoted video was initially played for at least 1ms. This excludes replays.',
  reachOrganic: 'The number of unique accounts who saw this post. Measures organic Reach only.',
  estimatedReach: 'The estimated number of unique accounts who saw this post',
  impressionsPromoted: 'The number of impressions from promoted posts',
  effectivenessOrganic:
    'The percentage of users that saw your organic post and engaged with it\n' +
    '\n' +
    'Effectiveness = (Likes + Comments + Saves + Video Views) / Est. Reach\n' +
    `\n${discontinuationNote}`,
  effectivenessUgc:
    'The percentage of people that saw your post and engaged with it\n' +
    '\n' +
    'Effectiveness = Likes + Comments / Est. Reach',
  engagementRateOrganic:
    'The organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Followers',
  engagementRateOrganicImpressions:
    'The organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Impressions\n' +
    `\n${discontinuationNote}`,
  engagementRateUgc:
    'The percentage of your followers that engaged with your post\n' +
    '\n' +
    'Engagement Rate = Likes + Comments / Followers',
  // Outdated tooltip no longer in use
  engagementsTotal: 'The total number of organic and promoted engagements on this post',
  engagementsOrganic:
    'The number of engagements on this post\n' +
    '\n' +
    'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)',
  engagementsOrganicNewMetrics:
    'The number of engagements on this post\n' +
    '\n' +
    'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)',
  engagementsPromoted:
    // Outdated tooltip no longer in use
    'The number of engagements on this post from promoted sources\n' +
    '\n' +
    'Engagements - Promoted = Likes - Promoted + Comments - Promoted',
  engagementsUgc:
    'The number of people who engaged with this post\n\nEngagements = Likes + Comments',
  likes: 'The number of people who liked this post',
  comments: 'The number of people who commented on this post',
  replays: `The number of rewatches for at least 1ms your video received in the same session\n\n${discontinuationNote}`,
  totalPlays:
    'The number of times this Reel was initially played for at least 1ms. Replays are counted after the initial play in the same Reel session.\n' +
    '\n' +
    'Note: This count may be greater than the sum of Replays and Plays because it includes Plays in the Facebook app through Cross App Recommendation.\n' +
    `\n${discontinuationNote}`,
  avgTimeViewedSec: 'The average time your video was played during a single viewing',
  totalTimeViewedSec: 'The sum of the time that all viewers watched your video for',
  emv: 'The earned media value of this post.\n\nEMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  followersGained:
    'The estimated number of followers this post gained for your brand. Only available for UGC from creators in Your Creators or Relationships.',
  impressions: discontinuationNote,
  effectivenessEngagements: `The percentage of users that saw this post and engaged with it

Effectiveness = Engagements / Reach`,
  engagementRateViews: `The percentage of users who engaged with this posts after seeing it

Engagement Rate (Views) = Engagements / Views`,
  views:
    'The number of times this video was played or replayed for 1 ms or more. Replays are counted after the initial play in the same video session.',
  viewsVideo:
    'The number of times your video was played or replayed for 1 ms or more. Replays are counted after the initial play in the same video session.',
  videoViewsPaid:
    'The number of times this video was initially played for at least 1ms. This excludes replays.',
  missingPublicViewsValue:
    'Views metric is not available for this Reel due to Instagram API limitations.',
  missingViewsEngagementRatePublicViewsValue:
    'Engagement Rate (Views) is unavailable for this Reel due to missing views data.',
  missingLikeEngagementRatePublicViewsValue:
    'Engagement Rate (Views) is unavailable for this Reel due to missing like data.',
  missingViewsAndLikeEngagementRatePublicViewsValue:
    'Engagement Rate (Views) is unavailable for this Reel due to missing views and like data.',
  sharesPaid:
    'Shares from Ad campaigns. May not match Instagram native or Business Manager insights for Boosted promotions due to API limitations.',
  savedPaid:
    'Saves from Ad campaigns. May not match Instagram native or Business Manager insights for Boosted promotions due to API limitations.',
  reposts: 'The number of times your post has been reposted',
  reelsSkipRate: 'The percentage of viewers who skipped this Reel within the first 3 seconds',
  crosspostedViews:
    'Total views across Instagram and Facebook for content crossposted natively between platforms. For Reels, includes views from crossposting or cross-app recommendations, where Reels appear on Facebook under your Instagram username.',
  facebookViews:
    'The number of views this post received on Facebook, for content crossposted natively from Instagram. For Reels, also includes views from cross-app recommendations, where Reels appear on Facebook under your Instagram username.',
});

const metaInstagramReelMediaPopupTooltips = Object.freeze({
  ...metaInstagramMediaPopup,
  avgTimeViewedSec: instagramReelsInsightsTooltips.avgTimeViewedSec,
  totalTimeViewedSec: instagramReelsInsightsTooltips.totalTimeViewedSec,
  totalPlays: instagramReelsInsightsTooltips.totalPlays,
  videoViewsPaid: instagramReelsInsightsTooltips.videoViewsPaid,
  videoPlaysTotalPaid:
    'The total number of times this video was played for at least 1ms. Excludes replays.',
  totalEngagementsPaid:
    'The total number of promoted engagements on this post\n' +
    '\n' +
    `Promoted Engagements = Likes + Comments`,
  effectiveness: instagramReelsInsightsTooltips.effectivenessOrganic,
  replays: instagramReelsInsightsTooltips.replays,
  effectivenessEngagements: instagramReelsInsightsTooltips.effectivenessEngagements,
  engagementRateViews: instagramReelsInsightsTooltips.engagementRateViews,
  views: instagramReelsInsightsTooltips.views,
  crosspostedViews: instagramReelsInsightsTooltips.crosspostedViews,
  facebookViews: instagramReelsInsightsTooltips.facebookViews,
  reelsSkipRate: instagramReelsInsightsTooltips.reelsSkipRate,
});

export const toolTips = {
  // Pinterest
  richPin:
    'Rich pins are only available for people that apply for them, and in that case all of their pins are rich pins. Linking to the validator would be confirmation that it will work on Pinterest.',
  newFollowers: 'Number of new followers of your account.',
  siteTraffic:
    'Traffic driven to your website from your pins and UGC, pulled from Google Analytics.',
  ugcTraffic:
    'Traffic driven to your site via user generated content, pulled from Google Analytics.',
  ugcPins: 'UGC are pins created by a user that direct to your website.',
  topPins:
    'Top pins from the past 7 days based on engagements. (Engagements = Pin Clicks + Outbound Clicks + Saves). These posts are compared to the Avg. Engagements in the last 12 weeks.',
  boardNeedsAccount: 'Connect your Pinterest account to choose a Pinterest Board',
  boardTraffic: 'Traffic to your website from pins, pulled from Google Analytics.',
  boardSaves: 'Number of times a user has saved your pin to their own board.',
  boardClicks: 'Number of clicks to your website from pins on your profile.',
  boardImpressions:
    'Number of times a pin showed up in the home feed, search results, and category feeds.',
  boardEngagementRate: 'Engagement Rate = (Clicks + Saves) / Impressions',

  // Pin metrics
  pinterestInsights: {
    insightsHeader: 'Insights data may be delayed up to 72 hours.',
    engagementRateVideo:
      'Percentage of Pinterest users that engaged with your video pin. (Engagement Rate = (Outbound Clicks + Saves) / Video Views)',
    engagementRateImage:
      'Percentage of Pinterest users that engaged with your photo pin. (Engagement Rate = (Pin Clicks + Outbound Clicks + Saves) / Impressions)',
    outboundClicks:
      'When a Pinterest user clicks and follows the link attached to your pin to the destination URL.',
    overviewLinkClicks:
      'When a Pinterest user clicks and follows the link attached to your pins (and saved pins) to the destination URL.',
    engagementLinkClicks:
      'When a Pinterest user clicks and follows the link attached to your owned pins to the destination URL.',
    saves: 'When a Pinterest user saves your pin.',
    impressions: 'When a Pinterest user has your pin displayed to them on Pinterest.',
    pinClicks: 'When a Pinterest user clicks your pin to see more details.',
    comments: 'When a Pinterest user comments on your pin.',
    videoViews: 'The amount of views for at least 2 seconds with 50% of the video pin in view.',
    averageWatchTime: 'The average time someone spent watching your video pin.',
    engagementRate:
      'Percentage of Pinterest users that engaged with your pin. (Engagement Rate = (Pin Clicks + Outbound Clicks + Saves) / Impressions)',
    engagements:
      'The total number of organic engagements your image, video and product Pins received. Engagements = Outbound Clicks + Saves + Pin Clicks',
    engagementsMedia:
      'The total number of engagements your Pin received. Total Engagements = Outbound Clicks + Saves + Pin Clicks',
  },

  // Tiktok metrics
  tiktokInsightsPromoted: {
    effectiveness:
      'The percentage of people that saw and engaged with this video. Effectiveness Rate = (Likes + Comments + Shares + Video Views) / Reach',
    engagementRate:
      'The percentage of people who engaged with your TikTok video through both organic and promoted reach.\nEngagement Rate = Engagements / Reach',
    retentionRate:
      'The average percentage of your videos that people watched.\nRetention Rate = Average Time Watched / Video Duration',
    totalEngagements:
      'The number of engagements this TikTok video received from both organic and promoted reach.\nEngagements = Likes + Comments + Shares + Favorites',
    ugcTotalEngagements:
      'The total number of engagements this video has received. Engagements = Likes + Comments + Shares',
    completionRate:
      'The percentage of viewers that watched your entire video through both organic and promoted reach.',
    favorites:
      'The number of times this TikTok video was added to favorites through both organic and promoted reach.',
    forYouPageViews: 'The total percentage of video views that came from the For You page.',
    followingPageViews:
      'The total percentage of video views that came from viewers on their Following page.',
    hashtagViews: 'The total percentage of video views that came from hashtags.',
    soundViews: "The total percentage of video views that came from the sound's page.",
    profileViews:
      'The total percentage of video views that came from viewers on your personal profile.',
    searchViews: 'The total percentage of video views that came from users performing a search.',
    entertainmentScore:
      'The entertainment measure of this video on a scale of 0 to 10. The higher the Engagement and Retention Rates of your video, the higher your score will be. Scores will be generated 48 hours after publishing.',
    emv: 'The earned media value of this post.\n\nEMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
    videoViews:
      'The number of times your TikTok video was played through both organic and promoted reach.\nA view is counted when someone watches for at least 1 second. Returning after swiping away counts as a new view.',
    organicViews:
      'The number of times your videos were viewed.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, this is an estimated value and the sum of organic + promoted views may not match the Video Views metric once the promotions end.',
    paidViews:
      'The number of times your videos were viewed.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted views may not match the Video Views metric once the promotions stop.',
    reach:
      'The number of people who watched your TikTok video at least once, across both organic and promoted reach.',
    organicReach:
      'The number of unique accounts that saw your posts.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted Reach may not match the Reach metric once the promotions stop.',
    paidReach:
      'The number of unique accounts that saw your posts.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted Reach may not match the Reach metric once the promotions stop.',
    organicEngagementRate:
      'The percentage of people who engaged with your posts after seeing them.\nEngagement Rate = Engagements / Reach',
    paidEngagementRate:
      'The percentage of people who engaged with your posts after seeing them.\nEngagement Rate = Engagements / Reach\n\n<strong>Important:</strong> This metric stops updating when ad promotions end.',
    viewsBasedEngagementRate:
      'The percentage of people who engaged with your TikTok video through both organic and promoted reach.\nEngagement Rate (V) = Engagements / Video Views',
    organicViewsBasedEngagementRate:
      'The percentage of people who engaged with your posts after seeing them.\nEngagement Rate = Engagements / Video Views',
    paidViewsBasedEngagementRate:
      'The percentage of people who engaged with your posts after seeing them.\nEngagement Rate = Engagements / Video Views\n\n<strong>Important:</strong> This metric stops updating when ad promotions end.',
    organicEngagements:
      'The total number of engagements your posts received.\nEngagements = Likes + Comments + Shares',
    paidEngagements:
      'The total number of engagements your posts received.\nEngagements = Likes + Comments + Shares\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted engagements may not match the Engagements metric once the promotions stop.',
    likes: 'The number of likes this TikTok video received from both organic and promoted reach.',
    organicLikes:
      'The number of likes your posts received.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted likes may not match the Likes metric once the promotions stop.',
    paidLikes:
      'The number of likes your posts received.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted likes may not match the Likes metric once the promotions stop.',
    shares:
      'The number of times this TikTok video was shared through both organic and promoted reach.',
    organicShares:
      'The number of times your posts were shared.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted shares may not match the Shares metric once the promotions stop.',
    paidShares:
      'The number of times your posts were shared.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted shares may not match the Shares metric once the promotions stop.',
    comments:
      'The number of comments this TikTok video received from both organic and promoted reach.',
    organicComments: 'The number of comments your posts received.',
    paidComments: 'The number of comments your posts received.',
    videoDuration: 'The length of your published videos during the reporting period.',
    totalTimeWatched:
      'The total amount of time viewers spent watching your video through both organic and promoted reach.',
    avgTimeWatched:
      'The average amount of time viewers spent watching your video through both organic and promoted reach.',
    newFollowers:
      'The number of new followers gained from this TikTok video through both organic and promoted reach.',
    organicNewFollowers:
      'The number of new followers gained organically.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted follows may not match the Follows metric once the promotions stop.',
    paidNewFollowers:
      'The number of new followers gained through promotions.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted follows may not match the Follows metric once the promotions stop.',
    totalProfileViews:
      'The number of profile views driven by this TikTok video through both organic and promoted reach.',
    organicProfileViews:
      'The number of profile views gained organically.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted profile views may not match the Profile Views metric once the promotions stop.',
    paidProfileViews:
      'The number of profile views gained through promotions.\n\n<strong>Important:</strong> This metric stops updating when ad promotions end. Due to API limitations, the sum of organic + promoted profile views may not match the Profile Views metric once the promotions stop.',
  },

  tiktokInsights: {
    effectiveness:
      'The percentage of people that saw and engaged with this video. Effectiveness Rate = (Likes + Comments + Shares + Video Views) / Reach',
    engagementRate: 'Engagement Rate = Engagements / Reach',
    viewsBasedEngagementRate: 'Engagement Rate = Engagements / Video Views',
    retentionRate:
      'The average percentage of this video that people watched. Retention Rate = Average Time Watched / Video Duration',
    totalEngagements:
      'The total number of engagements this video has received. Total Engagements = Likes + Comments + Shares + Favorites',
    ugcTotalEngagements:
      'The total number of engagements this video has received. Engagements = Likes + Comments + Shares',
    completionRate: 'The percentage of viewers that watched your entire video.',
    videoViews:
      'The number of times your TikTok video was played.\nA view is counted when someone watches for at least 1 second. Returning after swiping away counts as a new view.',
    reach: 'The number of people who watched your TikTok video at least once.',
    likes: 'The number of likes this TikTok video received.',
    shares: 'The number of times this TikTok video was shared.',
    comments: 'The number of comments this TikTok video received.',
    favorites: 'The number of times this TikTok video was added to favorites.',
    newFollowers: 'The number of new followers gained from this TikTok video.',
    totalProfileViews: 'The number of profile views driven by this TikTok video.',
    avgTimeWatched: 'The average amount of time viewers spent watching your video.',
    totalTimeWatched: 'The total amount of time viewers spent watching your video.',
    forYouPageViews: 'The total percentage of video views that came from the For You page.',
    followingPageViews:
      'The total percentage of video views that came from viewers on their Following page.',
    hashtagViews: 'The total percentage of video views that came from hashtags.',
    soundViews: "The total percentage of video views that came from the sound's page.",
    profileViews:
      'The total percentage of video views that came from viewers on your personal profile.',
    searchViews: 'The total percentage of video views that came from users performing a search.',
    entertainmentScore:
      'The entertainment measure of this video on a scale of 0 to 10. The higher the Engagement and Retention Rates of your video, the higher your score will be. Scores will be generated 48 hours after publishing.',
    emv: 'The earned media value of this post.\n\nEMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  },

  youtubeInsights: {
    insightsHeader: 'Insights data may be delayed up to 72 hours.',
    avgViewPercentage: 'The average percentage of your videos that people watched.',
    watchTime: 'The total amount of time that viewers watched videos on your channel.',
    subscribersGained: 'The number of viewers who subscribed while watching your videos',
    subscribersLost: 'The number of viewers who unsubscribed while watching your videos',
    totalEngagements:
      'The total number of engagements this video has received.\nEngagements = Likes + Dislikes + Comments + Shares',
    cardClicks: 'The number of times the cards were clicked on your videos',
    cardClickRate:
      'The click through rate for all cards on your videos\nCard Click Rate = Card Clicks / Card Impressions',
    cardImpressions: 'The number of times cards were displayed on your videos',
    cardTeaserClicks: 'The number of times the card teasers were clicked on your videos',
    cardTeaserClickRate:
      'The percentage of people who clicked on a card teaser after seeing it\nCard Teaser Click Rate = Card Teaser Clicks / Card Teaser Impressions',
    cardTeaserImpressions: 'The number of times card teasers were displayed on your videos',
    premiumViews: 'The number of times your videos were viewed by YouTube Premium members',
    premiumWatchTime: 'The total amount of time your videos were viewed by YouTube Premium members',
  },

  // LinkedIn metrics
  linkedinInsights: {
    clickThroughRate:
      'The percentage of your audience that clicked on this post after seeing it.\n\nClick Through Rate = Clicks / Impressions',
    clicks: 'The number of times people clicked on this post',
    comments: 'The total number of comments this post received',
    engagementRate: 'Engagement Rate = Total Engagements / Impressions',
    engagements: 'The total number of engagements on this post',
    impressions: 'The number of times this organic post has been viewed',
    reactions: 'The total number of reactions this post received',
    uniqueImpressions: 'The number of times this organic post was shown to unique LinkedIn members',
    videoViews:
      'The number of times this video has been viewed.\nA view is counted when at least 50% of the video is visible and playback is over 2 seconds.',
    watchTime: 'The sum of time that all viewers watched your video for',
  },

  threadsInsights: {
    views: 'The number of times this post has been displayed. Similar to Impressions.',
    likes: 'The number of likes this post has received',
    replies: 'The number of replies this post has received',
    reposts: 'The number of times this post was reposted',
    quotes: 'The number of times this post was quoted',
    shares: 'The number of times this post was shared',
    engagements:
      'The total number of engagements this post received\n\nEngagements = Likes + Replies + Reposts + Quotes',
    engagementRate:
      'The percentage of your audience that engaged with this post\n\nEngagement Rate = (Likes + Replies + Reposts + Quotes) / Views',
    repostsAndQuotes: 'The number of times this post was reposted or quoted',
  },

  instagramOverview: {
    totalVideoViews:
      'The total number times your organic and promoted videos published during the reporting period were played for at least 1ms. This excludes replays. This may include views from IGTV.',
    netNewFollowers:
      'The net total of followers your account has accumulated during the selected date range',
    totalEngagements:
      'The number of engagements on your posts\n' +
      '\n' +
      'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)',
    averageEngagementRate:
      'The average engagement rate for your organic posts published during the selected date range\n' +
      '\n' +
      'Engagement Rate = Engagements / Followers',
    averageEffectiveness:
      'The average effectiveness rate for your organic posts published during the reporting period\n' +
      '\n' +
      'Feed Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Reach\n' +
      '\n' +
      'Reels Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Est. Reach',
    averageReach:
      'The average reach for your organic posts published during the selected date range',
    impressions:
      'The total number of impressions for your organic and promoted posts published during the selected date range',
    followersChart:
      'The number of people who have followed your Instagram account during the selected date range',
    mostEngagingPosts:
      'Highest or lowest performing organic and promoted posts published during the selected date range by engagement rate. These posts are compared to the average engagement rate per post over the last 12 weeks.',
    topUGC: 'Top organic posts that featured you during the selected date range, sorted by reach',
    topLikeShopStats: 'Your LikeShop performance during the selected date range',
    topLikeShopContents:
      'Top performing LikeShop posts published during your selected date range. Posts are sorted by number of clicks.',
    mostEntertaining:
      'Your most entertaining Reels published during the reporting period. These posts are compared to the average value from posts published during the comparison period.',
    metrics: {
      SUM_TOTAL_ENGAGEMENTS:
        'The number of engagements your posts received\n' +
        '\n' +
        'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted Comments)\n' +
        '\n' +
        'Note: Promoted Unsaves and Reposts are unavailable',
      AVG_REACH: 'The average number of unique accounts that saw your organic posts',
      TOTAL_IMPRESSIONS: 'The number of times your organic and promoted posts have been viewed',
      TOTAL_LIKES: 'The total number of likes your organic and promoted posts have received',
      TOTAL_COMMENTS: 'The total number of comments left on your organic and promoted posts',
      SAVES: 'The number of times your organic posts have been saved',
      TOTAL_VIDEO_VIEWS: 'The number of times your organic and promoted videos have been viewed',
      AVG_ENGAGEMENT_RATE:
        'The average percentage of your audience that engaged with your organic posts published during the selected date range\n' +
        '\n' +
        'Engagement Rate = Engagements / Followers',
      AVG_EFFECTIVENESS:
        'The average percentage of users that saw and engaged with your organic posts published during the selected date range.\n' +
        '\n' +
        'Feed Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Reach\n' +
        '\n' +
        'Reels Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Est. Reach',
      VIEWS:
        "The number of times your posts were displayed on a person's screen. This includes repeat views",
      AVG_EFFECTIVENESS_ENGAGEMENTS:
        'The average percentage of users that saw your posts and engaged with them\n' +
        '\n' +
        'Avg. Effectiveness = Engagements / Reach',
      AVG_AVG_WATCH_TIME:
        'The average amount of time users spent playing your Reels in a single viewing',
    },
    views:
      "The number of times your posts were displayed on a person's screen. This includes repeat views",
    avgEffectivenessEngagements:
      'The average percentage of users that saw your posts and engaged with them\n' +
      '\n' +
      'Avg. Effectiveness = Engagements / Reach',
    shares: 'The number of times your posts were shared',
  },

  instagramInsights,

  instagramReelsInsights: instagramReelsInsightsTooltips,

  metaInstagramMediaPopup,

  metaInstagramReelMediaPopup: metaInstagramReelMediaPopupTooltips,

  instagramAdAccountScopedInsightsSuffixes,

  // Stories
  instagramStory: {
    impressions: discontinuationNoteStories,
    swipeUps:
      'Dash Social can only track link clicks for frames which were scheduled through the Dash Social Scheduler and have a trackable link enabled.',
    ugcStorySwipeUps: 'Due to API limitations, link click data is unavailable for UGC Stories',
    swipeUpsUnavailable:
      'Link click tracking is not included in your current package. Contact your customer success representative to upgrade or learn more.',
    exitRate: `The percentage of people who exited out of the story\n\nExit Rate = Exits / Impressions.\n\n${discontinuationNoteStories}`,
    completionRate: `The percentage of people who viewed the entire story\n\nCompletion Rate = (Impressions - Exits) / Impressions.\n\n${discontinuationNoteStories}`,
    replies:
      'Due to API restrictions, replies from users in the European Economic Area (EEA) or Japan will no longer be included. For stories created in the EEA or Japan, Replies will now return a value of 0.',
    exits:
      'The number of times a viewer has swiped out of the story or tapped the X in the upper-right corner of the story frame. An exit is counted even if it occurs at the last millisecond.',
    limitMessage:
      "You've reached your board limit. To upgrade your package please contact your Customer Success Representative.",
    trackableLink:
      'Link that Dash Social uses to track link clicks on this frame. Frames must have a trackable link in order to display link clicks.',
    emv: `The Earned Media Value of this story.\nEMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable stories.\n\n${discontinuationNoteStories}`,
    emvViews:
      'The Earned Media Value of this story.\nEMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable stories.',
    completionRateViews:
      'The percentage of people who viewed this entire story\n\nCompletion Rate = (Views - Exits) / Views\n\nThis metric is only available for reporting after March 24, 2025.',
    exitRateViews:
      'The percentage of people who exited out of this story\n\nExit Rate = Exits / Views\n\nThis metric is only available for reporting after March 24, 2025.',
    views:
      "The number of times this story was displayed on a person's screen. This includes repeat views.\n\nThis metric is only available for reporting after March 24, 2025.",
  },
  storyOverview: {
    yourStories:
      "Individual frames are grouped into a story when there's less than half an hour between when each frame is posted.",
    metrics: {
      storyFramesCount:
        'The total number of story frames published during the selected date range.',
      avgReach: 'The average reach of the story frames published during the selected date range.',
      totalImpressions:
        'The total number of impressions on the story frames published during the selected date range.',
      avgCompletionRate:
        'The average completion rate of the story frames published during the selected date range\n' +
        '\n' +
        'Completion Rate = (Impressions - Exits) / Impressions',
      avgExitRate:
        'The average exit rate of the story frames published during the selected date range.\n' +
        '\n' +
        'Exit Rate = Exits / Impressions',
      totalSwipeUps:
        'Total number of link clicks on the story frames published during the selected date range. Only link click links scheduled through the Dash Social scheduler are trackable via the platform.',
      totalReplies:
        'The total number of replies to the story frames published during the selected date range.\n' +
        '\n' +
        'Due to API restrictions, replies from users in the European Economic Area (EEA) or Japan will no longer be included. For stories created in the EEA or Japan, Replies will now return a value of 0.',
      totalViewsV2:
        'The total number of views on the story frames published during the selected date range.\n' +
        '\n' +
        'This metric is only available for reporting after March 24, 2025.',
      avgCompletionRateViews:
        'The average completion rate of the story frames published during the selected date range\n' +
        '\n' +
        'Completion Rate = (Views - Exits) / Views\n' +
        '\n' +
        'This metric is only available for reporting after March 24, 2025.',
      avgExitRateViews:
        'The average exit rate of the story frames published during the selected date range.\n' +
        '\n' +
        'Exit Rate = Exits / Views\n' +
        '\n' +
        'This metric is only available for reporting after March 24, 2025.',
    },
  },

  facebookAdsInsights: {
    total: 'Total insights across all ad placements',
    startDate: 'The start date of the ad set',
    endDate: 'The end date of the ad set',
    ctr: 'Link Click-Through Rate',
    cpc: 'Cost per Link Click',
    apiLimitationsMessage:
      'Due to API limitations we can only show all time results for unique channels',
  },

  facebookInsights: {
    comments:
      'The number of times people commented on your organic and promoted post. This metric does not include comments gained on posts through shares.',
    paidAndOrganicComments:
      'The number of times people commented on your organic and promoted post. This metric does not include comments gained on posts through shares.',
    reach: 'The reach from your organic and promoted post', // Should we change it?
    paidAndOrganicReachV2: 'The reach from your organic and promoted post',
    reactions:
      'The number of times people reacted to your original or promoted post. This metric includes reactions gained from reshares\n\nReactions = Likes, Love, Haha, Wow, Sad, Angry and Care',
    paidAndOrganicReactions:
      'The number of times people reacted to your original or promoted post. This metric includes reactions gained from reshares\n\nReactions = Likes, Love, Haha, Wow, Sad, Angry and Care',
    totalEngagements: 'Includes Reactions, Comments, Shares, and Post Clicks',
    paidAndOrganicEngagementsV2:
      'The sum of engagements on your organic and promoted post\n\nEngagements = Reactions + Shares + Comments\n\nReactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
    organicEngagementsV2:
      'The sum of engagements on your organic post\n\nEngagements = Reactions + Shares + Comments\n\nReactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
    paidEngagements:
      'The sum of engagements on your promoted post\n\nEngagements = Reactions + Shares + Comments\n\nReactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
    engagementRate: 'Engagement Rate = Total Engagements / Impressions',
    paidAndOrganicEngagementRateV2:
      'The average engagement rate for your organic and promoted post\n\nEngagement Rate = Engagements / Views',
    organicEngagementRateV2:
      'The engagement rate for your organic post\n\nEngagement Rate = Engagements / Views',
    paidEngagementRateV2:
      'The average engagement rate for your promoted post\n\nEngagement Rate = Engagements / Views',
    effectiveness: 'Effectiveness Rate = (Total Engagements + Video Views) / Reach',
    paidAndOrganicEffectivenessV2:
      'The percentage of users that saw your organic and promoted post and engaged with it\n\nEffectiveness = Engagements / Reach',
    organicEffectivenessV2:
      'The percentage of users that saw your organic post and engaged with it\n\nEffectiveness = Engagements / Reach',
    paidEffectivenessV2:
      'The percentage of users that saw your promoted post and engaged with it\n\nEffectiveness = Engagements / Reach',
    postClicks:
      'The number of times people clicked anywhere in your organic and promoted post without generating a story',
    paidAndOrganicPostClicks:
      'Number of times people clicked anywhere on your organic and promoted post without generating a story. This metric excludes reactions, comments, and shares.\n\nPost Clicks = Link Clicks + Photo View Clicks + Other Clicks',
    photoViewClicks:
      'The number of times someone clicked or tapped a photo in your organic and promoted post to open or view it in full-screen',
    paidAndOrganicPhotoViewClicks:
      'The number of times someone clicked or tapped a photo in your organic and promoted post to open or view it in full-screen',
    linkClicks:
      'When a Facebook user clicks and follows the link in your post or link preview to the destination URL',
    organicLinkClicks: 'The number of times people clicked on the link in a post or link preview',
    paidAndOrganicLinkClicks:
      'The number of times a Facebook user clicked and followed the link, or link preview to the destination URL on your promoted posts',
    otherClicks:
      'The number of times people clicked or tapped on your organic and promoted post that are not destination-link clicks. For example, tapping “See More”, the profile or page name, expanding media, or other interactive elements',
    paidAndOrganicOtherClicks:
      'The number of times people clicked or tapped on your organic and promoted post that are not destination-link clicks. For example, tapping “See More”, the profile or page name, expanding media, or other interactive elements',
    impressions:
      'The number of times your organic post was displayed on screen. One person may generate multiple impressions',
    shares:
      'The number of times people shared your original or promoted post. Shares of shared posts are not included',
    paidAndOrganicShares:
      'The number of times people shared your original or promoted post. Shares of shared posts are not included',
    organicVideoCompleteViews:
      'The number of times your video was played from the beginning to 95%, or more, of its length',
    organicVideoViews:
      'The total number of times this video was played for at least 3 seconds, or for nearly its total length if shorter than 3 seconds. This metric excludes replays.',
  },

  facebookReelsInsights: {
    plays:
      'The number of times your organic and promoted reel start to play after an impression was already counted. This is defined as reels sessions with 1ms or more of playback and excludes replays\n\nIncludes values from crossposting',
    replays:
      'The number of times your organic and promoted reel was rewatched. A rewatch is counted when your reel was played for at least 1ms in the same session\n\nIncludes values from crossposting',
    totalPlays:
      'The total number of views your video received. Includes initial plays and rewatches.',
    uniqueImpressions:
      'The number of people who saw your organic or promoted reel at least once, whether or not your reel was played. People reached is different from Views, which may include multiple views of your reel by the same people\n\nIncludes values from crossposting',
    avgTimeWatched:
      'The average time your organic and promoted reel was played during a single viewing',
    totalTimeWatched:
      'The sum of time that your organic and promoted reels was watched\n\nIncludes values from crossposting',
    follows:
      'The total count of people who began following your Page after viewing an organic or promoted reel, allowing your posts to appear in their Feed\n\nIncludes values from crossposting',
    percentageViewed: 'The percentage of this video your audience watched',
    effectivenessReels:
      "The rate of this post's views and engagement based on the unique accounts reached\n" +
      '\n' +
      'Effectiveness = (Total Engagements + Total Plays) / Reach',
  },

  facebookCompetitiveInsights: {
    totalComments: 'The total number of comments on this post, including threaded replies',
    totalReactions:
      'The number of times people clicked on like, care, love, wow, haha, sad, and anger reactions on your post',
    totalEngagements: 'Includes Reactions, Comments, and Shares',
    engagementRate: 'Engagement Rate = Total Engagements / Page Fans',
  },

  twitterInsights: {
    publicEngagements:
      'The total number of times a user interacted with this Post. Public Engagements = Replies + Likes + Repost + Quote Posts',
    engagementRate: 'Engagement Rate = Total Engagements / Impressions',
    impressions: 'The number of times your Post has been seen in timeline or search results.',
    profileClicks: 'The number of times your profile has been clicked from this Post.',
    totalEngagements:
      'The total number of times a user interacted with your Post, including reposts, replies, likes, and clicks on your Post.',
    userFollows:
      'The number of times your account has been followed from this Post. Only organic data is available.',
    videoViews: 'The number of times a video has been watched for at least two seconds.',
  },

  twitterCompetitiveInsights: {
    publicEngagements:
      'The total number of times a user interacted with this Post. Public Engagements = Replies + Likes + Repost + Quote Posts',
    replies: "The number of replies your competitor's post received",
    totalRetweets: "The total number of times your competitor's post was reposted or quote posted",
    likes: "The number of likes your competitor's post received",
    videoViewsTotal: "The number of video views your competitor's post accumulated",
  },

  facebook: {
    averageEngagementRate:
      'The average engagement rate for your organic posts published during the selected date range\n' +
      '\n' +
      'Engagement Rate = Total Engagements / Impressions',
    avgEngagementRate:
      'The average engagement rate for your organic posts published during the selected date range\n' +
      '\n' +
      'Engagement Rate = Total Engagements / Impressions',
    averageEngagementRatePublic:
      'The Total Engagements / Page Fans of posts published during the Reporting Period. This excludes Post Clicks and Impressions to ensure a fair comparison with competitors based on publicly available data.',
    averageEffectiveness:
      'The average effectiveness rate for your organic posts published during the selected date range\n' +
      '\n' +
      'Effectiveness Rate = (Total Engagements + Video Views) / Reach\n',
    avgEffectiveness:
      'The average effectiveness rate for your organic posts published during the selected date range\n' +
      '\n' +
      'Effectiveness Rate = (Total Engagements + Video Views) / Reach\n',
    linkClicks:
      'The total number of link clicks for your organic posts published during the selected date range',
    pageFansChart: 'The total number of people who have liked your Page',
    facebookYourPosts: 'Only organic facebook data will appear in Your Posts',
    netNewFans:
      'The net total of new fans your page has accumulated during the selected date range',
    newFans: 'The net total of new fans your page has accumulated during the selected date range',
    avgDailyPostReach:
      "The average daily reach for any of your page's organic posts during the selected date range",
    avgDailyReach:
      "The average daily reach for any of your page's organic posts during the selected date range",
    engagements:
      "The number of total engagements on any of your page's organic and paid posts during the selected date range",
    impressionsTooltip: `The total number of impressions for any of your page's organic posts during the selected date range`,
    impressions: `The total number of impressions for any of your page's organic posts during the selected date range`,

    metrics: {
      engagementsTooltip:
        'The number of reactions, comments, shares, and post clicks on your posts.',
      avg_engagement_rate:
        'The average engagement rate of the posts published during the selected date range\n' +
        '\n' +
        'Engagement Rate = Total Engagements / Impressions',
      avg_effectiveness:
        'The average effectiveness of the posts published during the selected date range\n' +
        '\n' +
        'Effectiveness Rate = (Total Engagements + Video Views) / Reach',
      reach: "The number of people who had any of your Page's posts enter their screen",
      link_clicks:
        'The number of times a Facebook user clicked and followed the link, or link preview to the destination URL on your organic and promoted posts',

      // V2 Tooltips
      total_engagements_posts_v2:
        'The sum of engagements on your organic and promoted posts' +
        '\n\n' +
        'Engagements = Reactions + Shares + Comments' +
        '\n\n' +
        'Reactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
      avg_engagement_rate_v2:
        'The average engagement rate for your organic and promoted posts' +
        '\n\n' +
        'Engagement Rate = (Engagements / Views) / Number of Posts',
      avg_effectiveness_v2:
        'The average percentage of users that saw your organic and promoted posts and engaged with it' +
        '\n\n' +
        'Average Effectiveness = (Engagements / Reach) / Number of Posts',
      paid_and_organic_post_reach_v2: 'The reach from your organic and promoted posts',
      paid_and_organic_views_by_post:
        'The number of times your organic and promoted videos were played for at least 1ms plus the number of times your photo and text posts were on screen',
    },
    topPosts:
      'Shows top or bottom performing posts based on engagements gained during the selected reporting period, compared to the daily average engagement of all posts in that same period.',
  },

  // Twitter
  twitter: {
    metricsDateSelectTooltip: `Data is only available up to ${maxTwitterDataCutOffDays} days prior to you connecting your X account to Dash Social.`,
    deleteThreadToolTip: `Delete threaded Post`,
    addThreadToolTip: `Add Post to Thread`,
    addThreadAutoPublishDisabled: `Auto Publish must be selected to add threaded Posts`,
    addThreadThreadLimitReached: `You cannot add more than ${maxTweetThreads} Posts when publishing a thread`,
    // TODO: Remove addThreadParentPostCrossChannelDisabled when cross brand is released
    addThreadParentPostCrossChannelDisabled: `Threaded posts are unavailable when multiple channels have been selected`,
    addThreadCrossChannelOrBrandDisabled: `Threaded posts are unavailable when multiple channels or brands have been selected`,
    sendNotificationDisabledForThreads: `Send Notification is unavailable when publishing a threaded Post`,
    followersChart: 'The total number of followers your X account has.',
    metrics: {
      engagements:
        'The total number of times a user interacted with your post, including reposts, replies, likes, and clicks on your post.',
      engagement_rate:
        'The engagement rate of posts that received engagement within the selected date range. Engagement Rate = Total Engagements / Impressions.',
      replies: 'The number of times the post has been replied to.',
      total_retweets: 'The number of times the post has been reposted or quote posted.',
      likes: 'The number of times the post has been liked.',
      impressions: 'The number of times your post has been seen in timeline or search results.',
      url_clicks:
        'The number of times a user clicks the link in your post and is sent to the destination URL.',
      user_profile_clicks: 'The number of times your profile has been clicked from a post.',
    },
    topPosts:
      'Top performing or lowest performing posts by total engagements published during the selected date range. These posts are compared to the average number of total engagements per post over the last 12 weeks.',
    cannotAddNonOriginalTweetToBoard:
      'Reposts, Quote Posts, and Replies cannot be added to a Board.',
    tooManyMentionsPerTweet: `You've exceeded the maximum limit of ${maxMentionsPerTweet} @mentions per Post.`,
    tooManyMentionsPerThread: `You've exceeded the maximum limit of ${maxMentionsPerTweet} @mentions per thread.`,
  },

  // TikTok
  tiktok: {
    followersChart:
      'The number of people who have followed your account during the selected date range.',
    metrics: {
      video_views:
        'The number of times your videos have been viewed during the selected date range.',
      single_post_video_views:
        'The number of times your video has been viewed during the selected date range.',
      engagements:
        'The total number of engagements on your videos during the selected date range. Total Engagements = Likes + Comments + Shares',
    },
    mostEntertaining:
      'Your most entertaining Videos published during the reporting period. These posts are compared to the average value from posts published during the comparison period.',
  },

  // landing page
  landingPage: {
    mostViewedVideos: 'Top performing videos across all channels, sorted by video views.',
    visionPredictedUGCVideo:
      'Vision predicted top UGC that either @mentioned or phototagged you during the reporting period, sorted by video views.',
  },

  // Scheduler
  addLinksTooltip: 'Add media to your post for the ability to add links.',
  featureNotIncludedInPlan: 'This feature isn’t included in your plan.',
  firstCommentOnlyAllowedForAutoPublish: 'First comment is only available for auto publishing.',
  hideProductMedia: 'Hide from Product Page Gallery.',
  igMediaCapacityExceeded: 'You can only select up to 10 photos and/or videos',
  illegalMediaSize: 'Instagram only allows one photo or video to be auto published at a time.',
  illegalTimeStamp: 'You must select a post time in the future.',
  insufficientPermission: "You don't have permission to auto publish posts for this brand.",
  insufficientPermissionForPostType:
    "You don't have permission to auto publish for this post type.",
  invalidLinkLength: 'Your link URL must be shorter than 2000 characters.',
  invalidLinkURL: 'Your link URL is not valid.',
  invalidLinkURLShortener: 'Your link URL is blocked because it may lead to spam.',
  maxDailyIGAutoPublishPostsReached: (postLimit) =>
    `You’ve reached the API limit of ${postLimit} auto published posts in 24 hr. Please use a push notification.`,
  maxIGCharactersReached: 'Your caption must be 2200 characters or less.',
  maxIGCommentCharactersReached: 'Your first comment must be 2200 characters or less.',
  maxIGHashtagsReached: 'You can only only add up to 30 hashtags.',
  maxLinkedInCharactersReached: 'Your caption must be 3000 characters or less.',
  maxLinkedInMediaLimit: 'Your post cannot contain more than 1 image',
  maxPinDescriptionCharactersReached: 'Your description must be 500 characters or less.',
  maxPinTitleCharactersReached: 'Your title must be 100 characters or less.',
  maxTikTokCharactersReached: 'Your caption must be 2200 characters or less.',
  tikTokDisabledTooltip: 'Only one video is supported per TikTok post.',
  youtubeDisabledTooltip: 'Only one video is supported per YouTube post.',
  videoDisabledTooltip: (platform) => `Only images are supported for ${platform} at this time`,
  imageDisabledTooltip: (platform) => `Only one video is supported per ${platform} post`,
  tikTokNoUserPermission: "You don't have the proper permissions to schedule TikTok posts.",
  reelsDisabledTooltip: 'Only one video is supported per Reel post.',
  linkedInDisabledTooltip: 'Only one image is supported per LinkedIn post.',
  mediaUploading: 'Your media is uploading...',
  noMediaSelected: 'You must select a video or image in order to publish now.',
  noMediaSelectedAutoPublish: 'You must select an image or video in order to auto publish.',
  noMediaSelectedTagProducts: 'You must select an image or a video in order to tag products.',
  noVideoSelectedAutoPublish: 'You must select a video in order to auto publish.',
  noVideoSelectedSave: 'You must select a video in order to proceed.',
  noPinterestRequiredFieldsAutoPublished:
    'For auto publishing this Pin, please make sure it has a title and has been added to a Pinterest Board.',
  noPinterestConnection:
    'To schedule a Pin, please connect your Pinterest account on the Platform Connections page..',
  notAutoPublish: 'Auto Publish must be selected to Publish Now.',
  autoPublishNotSupported: 'Auto Publish is not yet supported.',
  publishNowNotSupported: 'Publish Now is not yet supported.',
  noTwitterConnection:
    'To schedule an X post, please connect your Twitter account on the Platform Connections page.',
  noFacebookConnection:
    'To schedule a Facebook post, please connect your Facebook account on the Platform Connections page.',
  noLinkedinConnection:
    'To schedule a LinkedIn post, please connect your LinkedIn account on the Platform Connections page.',
  pinterestDestinationLink:
    "When possible, link directly to the source. Pinterest doesn't allow certain redirects or URL shorteners (like bit.ly).",
  productLinks:
    'Turns shopability of photos on or off. If photos have links assigned and capability is turned off, your photos will not redirect to pages you have assigned.',
  productMedia: 'Media that has been assigned the URL associated with this product.',
  showProductMedia: 'Show in Product Page Gallery.',
  trackableLink:
    'Convert this link to a tiny URL. This URL must be used in your Story to track link clicks in Dash Social.',
  trackableLinkPopup:
    'A trackable link will only be created for the first story frame. To track link clicks on each frame schedule frames separately.',
  updateLink: "We notice you've changed your media, please add a new link!",
  videoAutoPublishNotSupported: 'Video auto publishing is not supported',
  locationOnlyAllowedInInsAutoPublish: 'Adding a location is only available when auto publishing.',
  locationOnlyAllowedInFBAutoPublish: 'Check in is only available when auto publishing.',
  audienceOnlyAllowedInFBAutoPublish: 'Audience is only available when auto publishing.',
  errorLoadingAudienceCountries:
    'There was an issue loading the list of available Audience countries. Please try refreshing the page.',
  unableToSaveEmptyPost: 'This post is empty. Please add content.',
  invalidConnection: 'To schedule a post, you need to connect your account.',
  invalidTikTokConnection: 'To auto publish a post, you need to connect your TikTok account.',
  invalidTikTokMediaSelection:
    'You can only publish one video at a time on TikTok. Remove additional media to save your post.',
  invalidReelMediaSelection:
    'You can only publish one Reel at a time. Remove additional media to save your post.',
  invalidIGStoryMediaSelection: 'Only one image or video can be auto published at a time.',
  tikTokAllowTogglesOnlyAllowedInAutoPublish:
    'Select Auto Publish to customize Comment, Duet, and Stitch settings.',
  tagProductsOnlyAllowedInInsAutoPublish:
    'Tagging products is only available when auto publishing.',
  tagPeopleOnlyAllowedInInsAutoPublish: 'Tagging people is only available when auto publishing.',
  tagPeopleOnlyAllowedInInsWhenMediaSelected:
    'Tagging people is only available when media is selected.',
  tagPeopleOnlyAllowedInInsWhenAVideoIsSelected:
    'Tagging people is only available when a video is selected',
  instagramLink:
    'Dash Social Product Links are universal. Links added to this media will appear in any live Shoppable Gallery or Product Page Gallery where this media exists. If applicable, adding Links will also publish this media to your LikeShop.',
  tiktokLink:
    'Dash Social product links are universal. Links added to this media will appear in any live Shoppable Gallery or Product Page Gallery where this media exists.',
  shoppingTagsLoading: 'Your Shopping Tags are loading...',
  mediaConversionError:
    'There was an issue uploading this media to your Library. Please re-upload it to try again.',
  enableVision: 'To use Vision suggested cover images, you must enable auto publish.',
  promoteVision: 'Upgrade your plan to access Vision suggested thumbnails.',
  visionRemoveCoverImage:
    'To use Vision suggested cover images, you must remove your custom cover image.',
  crossBrandDisabledMedia:
    'Duplicating posts across brands is only available for uploaded pieces of media.',
  crossBrandDisabledTweetThread: 'Cross brand duplication is not supported for Post threads.',
  crossBrandDisabledPostStatus: 'Cross brand duplication is only supported for successful posts.',
  facebookGeogatingTooltip:
    'Select location(s) where your post will be visible. Only Facebook admins can view this post outside of the chosen locations.',
  limitLocationForFacebookReels: 'Limit by Location is not available for Facebook Reels',
  crossBrandLinkPreviewEditsDisabled:
    'Link preview customization is not supported for cross-brand posts.',
  // Library
  shoppableSettings: {
    mobileColumn: 'Choose how many columns to display when viewed on mobile devices.',
    mobileGap: 'Choose how much space to put between columns when viewed on mobile devices.',
    customLinks:
      'Turns shoppability on or off for this gallery. If photos have links assigned and shoppability is turned off, your photos will not redirect to pages you have assigned.',
  },
  onlyAddPostsWithMediatoGallery: 'You can only add posts with media to Galleries and Boards.',
  cannotAddDMedStories:
    'You can only add Instagram Stories from connected creators to Boards and Galleries.',
  cannotScheduleDMedStories: 'You can only schedule Instagram Stories from connected creators',
  cannotScheduleSnapchatStories: 'Snapchat Stories and Saved Stories cannot be scheduled',
  noAddPostsForReels: 'You cannot add posts to Reels.',
  customReorderDisabled: 'Sort by customized order to reorder your media.',
  noSchedulePermission: 'You do not have the proper permissions to use this feature.',
  onDifferentBrand:
    'To action this content, please ensure you are logged into the associated brand.',
  noScheduleForReels: 'Reels cannot be scheduled.',
  noBrandPermissionsForWidget:
    'Product Page Galleries are not included in your plan. Please contact your Customer Success Representative to add them.',
  noUserPermissionsForWidget:
    "You don't have access to this feature. Please contact your account administrator to enable access.",
  productTagIssue:
    'There was an issue with this product tag. Please remove the tag to save your post.',
  invalidaPlatformConnection:
    'Your account is disconnected. Your post may not publish as expected.',
  mediaPopupPublishDatesNoPermissionTooltip:
    'Contact your brand admin for permission to edit approved publishing dates',
  accountCannotGetBulkUpload: 'Brand must be in the Enterprise plan to use bulk uploading',
  tagCannotGetBulkUpload:
    'At least one brand in the tag must be in the Enterprise plan to use bulk uploading',
  brandCannotSelectBulkUpload:
    'Multi-brand upload is only available to brands on the Enterprise plan',
  instagramInviteCollaborators:
    'Invite Instagram users to collaborate and co-author this post to reach a broader audience and boost engagement',
  inviteCollaboratorsDisabled: 'Inviting collaborators is only available when auto publishing.',
  scheduledPostSaveDisabled:
    'There is a problem with one or more posts. Please resolve any issues before proceeding.',
  autoPublishDisabled: 'All posts must be set for auto publish to publish now',
  tiktokBrandedContentDisabledTooltip:
    'Brand content tags can only be applied when Auto Publish is enabled.',
  youtubeAudienceTooltip:
    "You’re required to tell YouTube whether your video is made for kids. This ensures compliance the Children's Online Privacy Protection Act (COPPA) and/or other laws.",
  // Instagram
  VIQ: {
    engagement: 'The percentage of your total owned engagement that comes from this segment.',
    photos: 'The percentage of your owned photos that live in this segment.',
    processingTrendAccount: 'Processing data for this account',
  },
  instagramEngagementRate:
    'The Average Engagement of a board will only include engagement from posts created by business accounts.',

  // Add to board limit
  addToBoardSourceLimit: {
    instagram: 'Only Instagram posts can be added to Instagram Boards.',
    instagramStory: 'Only Instagram stories can be added to Story Boards.',
    pinterest: 'Only Pinterest pins can be added to Pinterest Boards',
    uploaded: 'Only uploaded media can be selected.',
  },

  // Wrong type in library
  addToBoardWrongType: {
    expectImage: 'Only images can be published to this channel.',
    expectVideo: 'Only videos can be published to this channel.',
  },

  mustClear: {
    noBrandTagsAccess: 'You must clear the selected brands to choose tags.',
    noBrandAccess: 'You must clear the selected tags to choose brands.',
    noCompetitorTagsAccess: 'You must clear the selected tags to choose competitors.',
    noCompetitorAccess: 'You must clear the selected competitors to choose tags.',
    noBrandTagsAccessSettings: 'Brand must be in the Enterprise plan to use brand tags',
  },

  // Dashboards
  instagramDashboard: {
    followerGrowthRate:
      'The percentage your followers increased by during the Reporting Period. Follower Growth Rate = (New Followers - Old Followers) / Old Followers x 100%',
    followersLost:
      'The total numbers of users who unfollowed your account during the Reporting Period.\n' +
      '\n' +
      '*Lost Followers data is received once per day in the Pacific Timezone.',
    profileViews: 'The amount of times your profile has been viewed during the Reporting Period.',
    websiteClicks:
      'The amount of clicks on the website link in your profile during the Reporting Period.',
    emailContactClicks:
      'The amount of clicks on the email link in your profile during the Reporting Period.',
    textMessageClicks:
      'The amount of clicks on the text message link in your profile during the Reporting Period.',
    getDirectionClicks:
      'The amount of clicks on the directions link in your profile during the Reporting Period.',
    totalEngagements:
      'The total number of organic engagements from all posts published during the Reporting Period.',
    sumTotalEngagements:
      'The total number of engagements from all posts published during the Reporting Period.',
    avgEngagementRate:
      'The average percentage of your audience that engaged with your posts published during the Reporting Period. Engagement Rate = Organic Engagements / Followers',
    avgReach:
      'The average number of unique accounts that saw your posts published during the Reporting Period.',
    avgEffectiveness:
      'The average percentage of users that saw and engaged with your posts published  during the Reporting Period. Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Reach',
    avgEffectivenessNew:
      'The average effectiveness rate for your organic posts published during the reporting period.\n' +
      '\n' +
      'Feed Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Reach\n' +
      '\n' +
      'Reels Effectiveness Rate = (Likes + Comments + Saves + Video Views) / Est. Reach',
    avgEntertainmentScore:
      'The average Entertainment Score of your Reels published during the reporting period. The score is on a scale of 0 to 10 and is based on volume of organic engagements compared to reach.',
    avgEstimatedReach:
      'The average number of unique accounts that saw your posts published during the Reporting Period.\n' +
      "The average estimated number of unique accounts that saw competitor's posts based on their number of followers during the Reporting Period.",
  },
  instagramUGCDashboard: {
    numberOfPosts: 'The total number of posts that mentioned or tagged you.',
    estimatedOrganicReach:
      'The estimated number of unique accounts who saw posts that mentioned or tagged you.',
  },
  instagramStoriesDashboard: {
    avgReach:
      'The average number of unique accounts that saw your stories published during the Reporting Period.',
    exits:
      'The total number of times that a user has swiped out of the story or tapped the X in the upper-right corner of the story frame. An exit is counted even if it occured in the last millisecond.',
    avgExitRate:
      'The percentage of people who exited out of the story.\n' +
      '\n' +
      'Exit Rate = Exits / Impressions.',
    avgCompletionRate:
      'The average percentage of people who viewed the entire story.\n' +
      '\n' +
      'Completion Rate = (Exits - Impressions) / Impressions.',
    swipeUps:
      'Total number of link clicks on the story frames published during the selected date range. Only link click links scheduled through the Dash Social scheduler are trackable via the platform.',
  },
  benchmarksDashboard: {
    industryBenchmarks:
      'Industry benchmarks are an average—calculated from a curated set of brands within a specific industry',
    disabledStateMaxBenchmarks: 'You have selected the maximum number of benchmarks and/or brands',
    disabledStateSelectBenchmark: 'You must select a benchmark to add another',
  },
  facebookDashboard: {
    pageFanGrowthRate:
      "The percentage your Page's Fans increased by during the Reporting Period. Page Fans Growth Rate = (New Page Fans - Old Page Fans) / Old Page Fans x 100%",
    totalEngagements:
      'The total number of reactions, comments, shares, and post clicks on all posts published during the Reporting Period.',
    reactions:
      'The number of times people clicked on like, care, love, wow, haha, sad, and anger reactions on posts published during the Reporting Period.',
    postClicks:
      'The number of times a user clicked anywhere on your posts published during the Reporting Period.',
    photoViewClicks:
      'The number of times a user clicked on a photo for posts published during the Reporting Period.',
    linkClicks:
      'The number of times people clicked on the link in a post or link preview for posts published during the Reporting Period.',
    otherClicks:
      'The number of times people clicked on your posts published during the reporting period, such as clicks on your profile name or to "see more".',
    avgEngagementRate:
      'The average engagement rate of the posts published during the Reporting Period. Engagement Rate = (Total Engagements / Impressions)',
    avgEffectiveness:
      'The average effectiveness rate of the posts published during the Reporting Period. Effectiveness Rate = (Total Engagements + Video Views) / Reach',
    avgDailyPostReach:
      'The average number of unique accounts that saw your posts published during the Reporting Period.',
    videoCompleteViews:
      'The number of times your videos published during the reporting period were played from the beginning to 95%, or more, of its length.',
    comments: 'The number of comments any of your posts received during the Reporting Period.',
  },
  pinterestDashboard: {
    followerGrowthRate:
      'The percentage your followers increased by during the Reporting Period. Follower Growth Rate = (New Followers - Old Followers) / Old Followers x 100%',
    avgEngagementRate:
      'The average percentage of your audience that engaged with your Pins during the Reporting Period. Engagement Rate = (Pin Clicks + Outbound Clicks + Saves) / Impressions',
    totalAvgEngagementRate:
      'The average percentage of your audience that engaged with your Pins published during the Reporting Period. Engagement Rate = (Pin Clicks + Outbound Clicks + Saves) / Impressions',
    linkClicks:
      'The number of clicks on the links in your Pins published during the Reporting Period.',
    linkClicksAllPosts:
      'The number of clicks on the links on your Pins during the Reporting Period.',
    numberOfPosts: 'The number of Pins published during the Reporting Period.',
    closeups:
      'The number of times your Pins were clicked to view more details during the Reporting Period.',
    saves: 'The number of times users saved your Pins during the Reporting Period.',
  },
  twitterDashboard: {
    followerGrowthRate:
      'The percentage your followers increased by during the Reporting Period. Follower Growth Rate = (New Followers - Old Followers) / Old Followers x 100%',
    totalEngagementsAllPosts:
      'The total number of times a user interacted with your posts during the Reporting Period, including Reposts, replies, likes, hashtags, or link clicks.',
    avgEngagementRate:
      'The average engagement rate of your posts during the Reporting Period. Engagement Rate = Total Engagements / Impressions',
    totalAvgEngagementRate:
      'The average engagement rate of your posts published during the Reporting Period. Engagement Rate = Total Engagements / Impressions',
    totalRetweetsAllPosts:
      'The total number of times your Posts were Reposted during the Reporting Period. Total Reposts = Reposts + Quote Posts',
    profileClicksAllPosts:
      'The number of times your profile has been clicked from Posts during the Reporting Period.',
    userFollowsAllPosts:
      'The number of times your account has been followed from Posts during the Reporting Period.',
    likesAllPosts: 'The number of likes your Posts received during the Reporting Period.',
    linkClicksAllPosts:
      'The number of times links on your Posts were clicked during the Reporting Period.',
    QuotePostsAllPosts:
      'The number of times your Posts were Quote Posted during the Reporting Period.',
    repliesAllPosts: 'The number of replies your Posts received during the Reporting Period.',
    retweetsAllPosts: 'The number of times your Posts were Reposted during the Reporting Period.',
    videoViewsAllPosts:
      'The number of times your organic videos were viewed during the Reporting Period.',
  },
  tiktokDashboard: {
    netNewFollowers:
      'The net new number of people who have followed your account during the Reporting Period.',
    profileViews: 'The number of times your profile was viewed during the Reporting Period.',
    allPostVideoViews:
      'The number of times any of your organic and promoted videos were viewed during the Reporting Period.',
    allPostLikes:
      'The number of likes any of your organic and promoted videos received during the Reporting Period.',
    allPostComments:
      'The number of comments any of your organic and promoted videos received during the Reporting Period.',
    allPostShares:
      'The number of times any of your organic and promoted videos were shared during the Reporting Period.',
    engagements:
      'The total number of engagements any of your organic and promoted videos received during the Reporting Period.\n' +
      '\n' +
      'Total Engagements = Likes + Comments + Shares',
    followerGrowthRate:
      'The percentage your total followers increased by during the Reporting Period.\n' +
      '\n' +
      'Follower Growth Rate = (Current Period - Previous Period) / Previous Period x 100%',
    netGrowthRate:
      'The percentage your net new followers increased by during the Reporting Period.\n' +
      '\n' +
      'Growth Rate = (Current Period - Previous Period) / Previous Period x 100%',
    averageReach:
      'The average Reach of your organic and promoted videos published during the Reporting Period.',
    averageEngagementRate:
      'The average Engagement Rate of your organic and promoted videos published during the Reporting Period.\n' +
      '\n' +
      'Engagement Rate = Total Engagements / Reach',
    averageRetentionRate:
      'The average Retention Rate of the organic and promoted videos published during the Reporting Period.\n' +
      '\n' +
      'Retention Rate = Average Time Watched / Video Duration',
    averageFYPViews:
      'The average percentage of views that came from the For You page for organic and promoted videos published during the Reporting Period.',
    videoViewsTotal:
      'The total number of times your organic and promoted videos published during the Reporting Period were viewed.',
    likesTotal:
      'The total number of likes your organic and promoted videos published during the Reporting Period received.',
    commentsTotal:
      'The total number of comments your organic and promoted videos published during the Reporting Period received.',
    sharesTotal:
      'The total number of shares your organic and promoted videos published during the Reporting Period received.',
    totalEngagementsTotal:
      'The total number of engagements your organic and promoted videos published during the Reporting Period received.\n' +
      '\n' +
      'Total Engagements = Likes + Comments + Shares',
    avgTimeWatched:
      'The average amount of time viewers spent watching your organic and promoted videos published during the Reporting Period.',
    avgCompletionRate:
      'The average percentage of viewers that watched each organic and promoted video published during the Reporting Period to completion.',
    avgVideoViews:
      'The average number of times your organic and promoted videos published during the Reporting Period were viewed.',
    avgLikesTotal:
      'The average number of likes your organic and promoted videos published during the Reporting Period received.',
    avgCommentsTotal:
      'The average number of comments your organic and promoted videos published during the Reporting Period received.',
    avgSharesTotal:
      'The average number of shares your organic and promoted videos published during the Reporting Period received.',
    avgEngagementsTotal:
      'The average number of engagements your organic and promoted videos published during the Reporting Period received. Total Engagements = Likes + Comments + Shares',
    avgEntertainmentScore:
      'The average Entertainment Score of your organic and promoted videos published during the reporting period. The score is on a scale of 0 to 10 and takes the engagement and retention rates of each video into account. Scores are generated 48 hours after publishing.',
  },
  youTubeDashboard: {
    totalEngagements:
      'The total number of engagements your organic and promoted videos published during the Reporting Period received.\n' +
      '\n' +
      'Total Engagements = Likes + Dislikes + Comments + Shares',
    subscribersTotal:
      'The number of users who have subscribed to your channels by the last day of the Reporting Period.',
    subscriberGrowthRate:
      'The percentage your subscribers have increased by during the Reporting Period.\n' +
      '\n' +
      'Subscriber Growth Rate = (Current Period - Previous Period) / Previous Period x 100%',
    subscribersNetNew:
      'The net new number of users who have subscribed to your channel during the Reporting Period.',
    engagements:
      'The total number of engagements any of your organic and promoted videos received during the Reporting Period.\n' +
      '\n' +
      'Total Engagements = Likes + Dislikes + Comments + Shares',
    estimatedSecondsWatched:
      'The total amount of time that viewers watched any organic and promoted videos on your channel during the Reporting Period.',
    totalEstimatedSecondsWatched:
      'The total amount of time that viewers watched organic and promoted videos on your channel that were published during the Reporting Period.',
    views:
      'The number of times your organic and promoted videos were viewed during the Reporting Period.',
    dislikes:
      'The number of dislikes any of your organic and promoted videos received during the Reporting Period.',
    likes:
      'The number of likes any of your organic and promoted videos received during the Reporting Period.',
    comments:
      'The number of comments any of your organic and promoted videos received during the Reporting Period.',
    avgViewDuration:
      'The average amount of time viewers spent watching any of your organic and promoted videos during the Reporting Period.',
    dislikeTotal:
      'The total number of dislikes your organic and promoted videos published during the Reporting Period received.',
    addedToPlaylistTotal:
      'The total number of times your organic and promoted videos published during the Reporting Period were added to a playlist.',
    avgViewDurationTotal:
      'The average amount of time viewers spent watching your organic and promoted videos published during the Reporting Period',
    videoViewsTotal:
      'The total number of times your organic and promoted videos published during the Reporting Period were viewed.',
    likesTotal:
      'The total number of likes your organic and promoted videos published during the Reporting Period received.',
    commentsTotal:
      'The total number of comments your organic and promoted videos published during the Reporting Period received.',
    sharesTotal:
      'The total number of shares your organic and promoted videos published during the Reporting Period received.',
    avgPercentViewedTotal:
      'The average retention rate of your organic and promoted videos published during the Reporting Period.',
    avgViewPercentage:
      'The average retention rate of your organic and promoted videos during the Reporting Period.',
    shares:
      'The number of shares any of your organic and promoted videos received during the Reporting Period.',
    cardClickRate:
      'The average cards click-through-rate on all your organic and promoted videos during the Reporting Period.\n' +
      '\n' +
      'Card Click Rate = Card Clicks / Card Impressions',
    avgCardClickRate:
      'The average cards click-through-rate on your organic and promoted videos published during the Reporting Period.\n' +
      '\n' +
      'Card Click Rate = Card Clicks / Card Impressions',
    cardClicks:
      'The number of times the cards were clicked on all your organic and promoted videos during the Reporting Period.',
    totalCardClicks:
      'The number of times the cards were clicked on your organic and promoted videos published during the Reporting Period.',
    cardImpressions:
      'The number of times cards were displayed on all your organic and promoted videos during the Reporting Period.',
    totalCardImpressions:
      'The number of times cards were displayed on your organic and promoted videos published during the Reporting Period.',
    cardTeaserClickRate:
      'The average card teasers click-through-rate on all your organic and promoted videos during the Reporting Period.\n' +
      '\n' +
      'Card Teaser Click Rate = Card Teaser Clicks / Card Teaser Impressions',
    avgCardTeaserClickRate:
      'The average card teasers click-through-rate on your organic and promoted videos published during the Reporting Period.\n' +
      '\n' +
      'Card Teaser Click Rate = Card Teaser Clicks / Card Teaser Impressions',
    cardTeaserClicks:
      'The number of times the card teasers were clicked on all your organic and promoted videos during the Reporting Period.',
    totalCardTeaserClicks:
      'The number of times the card teasers were clicked on your organic and promoted videos published during the Reporting Period.',
    cardTeaserImpressions:
      'The number of times card teasers were displayed on all your organic and promoted videos during the Reporting Period.',
    totalCardTeaserImpressions:
      'The number of times card teasers were displayed on your organic and promoted videos published during the Reporting Period.',
    estimatedPremiumSecondsWatched:
      'The total amount of time your organic and promoted videos were viewed during the Reporting Period by YouTube Premium members.',
    totalEstPremiumSecondsWatched:
      'The total amount of time your organic and promoted videos published during the Reporting Period were viewed by YouTube Premium members.',
    premiumViews:
      'The number of times your organic and promoted videos were viewed during the Reporting Period by YouTube Premium members.',
    totalPremiumViews:
      'The number of times your organic and promoted videos published during the Reporting Period were viewed by YouTube Premium members.',
    videosAddedToPlaylist:
      'The number of times any of your organic and promoted videos were added to any YouTube playlists during the Reporting Period.',
  },
  sentimentDashboard: {
    commentSentimentPositiveRate:
      'The percentage of posts published during the reporting period with an overall positive comment sentiment',
    commentSentimentNeutralRate:
      'The percentage of posts published during the reporting period with an overall neutral comment sentiment.',
    commentSentimentNegativeRate:
      'The percentage of posts published during the reporting period with an overall negative comment sentiment.',
  },
  multiChannelDashboard: {
    totalEngagements:
      'The total number of engagements from all posts published during the Reporting Period.',
    followerGrowthRate:
      'The percentage your followers/Page Fans increased by during the Reporting Period.',
    avgEngagementRate:
      'The average percentage of your audience that engaged with your posts published during the Reporting Period.',
    avgReach:
      'The average number of unique accounts that saw your posts published during the Reporting Period.',
    avgEffectiveness:
      'The average percentage of users that saw and engaged with your posts published during the Reporting Period.',
    linkClicks:
      'The number of times people clicked on the link(s) in your posts during the Reporting Period.',
    netNewFollowers:
      'The net new number of people who have followed your account during the Reporting Period.',
    profileViews: 'The number of times your profile was viewed during the Reporting Period.',
    totalLikes:
      'The total number of likes your organic and promoted videos published during the Reporting Period received.',
    totalComments:
      'The total number of comments your organic and promoted videos published during the Reporting Period received.',
    totalVideoViews:
      'The total number of times your organic and promoted videos published during the Reporting Period were viewed.',
    totalShares:
      'The total number of shares your organic and promoted videos published during the Reporting Period received.',
    totalAvgViewDuration:
      'The average amount of time viewers spent watching your organic and promoted videos published during the Reporting Period',
    totalAvgPercentViewed:
      'The average retention rate of your organic and promoted videos published during the Reporting Period.',
  },
  accountDisconnection: {
    instagram:
      'Your Instagram account has been disconnected. Please reconnect to see applicable data.',
    instagram_stories:
      'Your Instagram account has been disconnected. Please reconnect to see applicable data.',
    instagram_story:
      'Your Instagram account has been disconnected. Please reconnect to see applicable data.',
    instagram_ugc:
      'Your Instagram account has been disconnected. Please reconnect to see applicable data.',
    instagram_competitive:
      'Your Instagram account has been disconnected. Please reconnect to see applicable data.',
    instagram_benchmarks:
      'Your Instagram account has been disconnected. Please reconnect to see applicable data.',
    facebook:
      'Your Facebook account has been disconnected. Please reconnect to see applicable data.',
    pinterest:
      'Your Pinterest account has been disconnected. Please reconnect to see applicable data.',
    twitter: 'Your X account has been disconnected. Please reconnect to see applicable data.',
    tiktok: 'Your TikTok account has been disconnected. Please reconnect to see applicable data.',
    youtube: 'Your Youtube account has been disconnected. Please reconnect to see applicable data.',
    all: `You haven't connected any accounts. Use the connection buttons below to connect each account.`,
  },

  // 360 Reports
  engagementFormula: {
    instagram: 'Sum of likes + comments + saves + video views on content published this month.',
    facebook: 'Sum of reactions + comments on content published this month.',
    twitter: 'Sum of reposts + likes on content published this month.',
    pinterest: 'Sum of pin clicks + Outbound clicks + saves this month.',
    engagementRate: 'Engagement Rate = (Likes + Comments) / Followers',
    effectivenessRate: 'Effectiveness Rate = (Likes + Comments + Video Views + Saves) / Reach',
  },

  citations: {
    facebookFans: 'Page Fans: The total number of people who have liked your Page.',
  },

  // Twitter Reports
  weeklyReport: {
    percentage: 'The green / red percentages are for the last week.',
    average: 'The averages are for the last 12 weeks.',
  },

  // Ads
  auto: 'This board will automatically pull in ads associated with your chosen filters.',
  manual: 'Add content manually to a board via Your Ads.',

  // Relationships
  instagramRelationship: {
    avgTotalEng:
      'Average sum of the likes, comments and saves on the influencer’s posts over the last 12 weeks.',
    avgReach: "The average number of unique people who have viewed this influencer's posts.",
    avgEstimatedReach:
      "The average number of estimated unique people who have viewed this influencer's posts.",
    disabledMentionsFilterText:
      'Connect your Instagram account to filter by users who mentioned you.',
    avgEngRate:
      'The average engagement rate for your posts published during the selected date range that feature this creator. This engagement rate uses public data only for comparison purposes.\n' +
      '\nEngagement Rate - Public = (Likes + Comments) / Followers',
  },

  // Top Posts Charts
  mostEntertaining: {
    entertainmentScore: 'Entertainment Score',
    videoViews: 'Video Views',
    views: 'Views',
  },

  // Social Listening
  socialListening: {
    topPostsPage:
      'The most engaging posts within this Trend published during the reporting period.',
    topPostsPreview:
      'The posts in Trends published during the selected date range, sorted by most engagements.',
    topPostsQuickSearch:
      'The most engaging posts matching your search criteria during the reporting period.',
    visualTrends:
      'The most visually similar content segments, based on posts published during the selected date range.',
    visualTrendsPopup:
      'Public posts from within Social Trends that have been grouped together based on their visual similarity.',
    disabledTooltipText: {
      trendsPage: 'Remove keyword to use other filters.',
      trendingKeywordPage: 'To change this filter, go back to Trends page.',
      topicCreatorPreviewPage: 'To change this filter, go back to the Results Preview.',
      topPostsPage: 'To change this filter, go back to Trends page.',
      webResultsPage: 'Web source cannot be edited after Topic creation.',
      topicFilterOff:
        'Create an email alert for when this Topic receives a higher volume of social mentions than usual.',
      topicFilterOn:
        'This Topic is being monitored for a change in volume based on your search criteria.',
    },
  },

  // Sentiment
  sentiment: {
    captionDescription:
      'The sentiment analysis for the caption of this post. Sentiment labels can be modified on a per-post basis for UGC posts.',
    commentsDescription: 'The overall sentiment analysis of the comments left on this post.',
    repliesDescription: 'The overall sentiment analysis of the replies left on this post.',
    publicDescription: 'The sentiment analysis for the caption of this post.',
    noComments: "This post doesn't have any comments on it yet.",
    noReplies: "This post doesn't have any replies on it yet.",
    cannotCalculateSentiment: 'Overall sentiment can not be calculated for this post.',
    overallSentimentProcessing:
      'Overall sentiment is currently processing. Check back a few minutes.',
  },

  // Vision AI
  visionAi: {
    disabledSourceButton: "You're currently viewing the source for this summary",
    activeSourceButton: 'View summary Source',
    downloadButton: 'Download as .png',
  },

  // Approvals
  requiresApproval: 'This post needs approval before publishing',

  // Content Automation
  brandCannotAccessContentAutomation: 'Brand must have access to content automations',
  tagBrandsCannotAccessContentAutomation:
    'At least one brand in the tag must have access to content automations',
  cannotCreateContentAutomationWithBrandTags:
    'You don’t have access to create an automation with Brand Tags',

  // Custom Metrics
  customMetrics: {
    brandCannotAccessCustomMetrics: 'Brand must have access to custom metrics',
    tagCannotAccessCustomMetrics:
      'At least one brand in the tag must have access to custom metrics',
    usageLimitReached: 'Custom Metric limit reached. A brand can only have 25 custom metrics',
    userCannotAccessOrganizationsCustomMetrics:
      "You do not have the required permissions to edit this brand's custom metrics",
  },
};

export const DEFAULT_VALIDATION_TEMPLATES = {
  required: 'This field is required',
  email: 'Please enter a valid email address.',
  minLength: 'This field must be at least {min} characters.',
  maxLength: 'This field cannot be more than {max} characters.',
};

export const formValidationMessages = {
  required: 'This field is required',
  strongPassword:
    'Please choose a password that contains at least one letter and one number, or, at least one lowercase and one uppercase letter.',
  wrongPassword: 'The old password you submitted is incorrect.',
  passwordMismatch: 'Passwords do not match.',
  minLength: 'Password must be at least eight characters.',
  email: 'Please enter a valid email address.',
  uniqueBrandEmail: 'This email address already belongs to a user on this brand.',
  name: 'Name is required.',
};

export const SERVER_ERROR = 'An error has occurred.';

export const dashboardFormFailureMessages = {
  serverError: 'An error occurred while attempting to save your dashboard.',
};

export const reportFormFailureMessages = {
  serverError: 'An error occurred while attempting to save your report.',
};

export const createBoardFailedMessages = {
  duplicate:
    "Could not save board.. Try a different board name (you've already got one like this).",
  deleted:
    'Psst! You recently deleted a board with the same name.. If you want to restore it, reach out to our support team.',
};

export const linkValidationMessages = {
  empty: 'Link is required',
  invalidLink: 'Your link URL is not valid',
  linkUpdateFailure: 'Failed to save because one or more of your links is invalid',
};

export const productUrlValidationMessages = {
  empty: 'URL is required',
  invalidUrl: 'Your product URL is not valid',
};

export const discardConfirmTitle = 'Discard Changes?';
export const discardCommentConfirmTitle = 'Discard Comment?';
export const discardUploadConfirmTitle = 'Exit Upload Process?';
export const discardConfirmMessage = 'If you exit now, your unsaved changes will be discarded.';
export const discardCommentConfirmMessage =
  'If you exit now, your unsaved comment will be discarded.';
export const discardUploadConfirmMessage =
  "If you leave now, your media won't be uploaded. Are you sure you want to proceed?";
export const discardConfirmButton = 'Discard';
export const saveApplyConfirmTitle = 'Save & Apply Changes?';
export const saveApplyConfirmButton = 'Save & Apply';
export const dataUnavailableMessage = 'Data not available';

export const mediaSourceType = {
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  PINTEREST: 'PINTEREST',
  FACEBOOK: 'FACEBOOK',
  FACEBOOK_ADS: 'FACEBOOK_ADS',
  FACEBOOK_LINK: 'FACEBOOK_LINK',
  FACEBOOK_TEXT: 'FACEBOOK_TEXT',
  FACEBOOK_TEXT_LINK: 'FACEBOOK_TEXT_LINK',
  TIKTOK: 'TIKTOK',
  TWITTER: 'TWITTER',
  TWITTER_LINK: 'TWITTER_LINK',
  TWITTER_TEXT: 'TWITTER_TEXT',
  YOUTUBE: 'YOUTUBE',
  LINKEDIN: 'LINKEDIN',
  UGC: 'UGC',
};

export const constants = {
  UPLOADED: 'UPLOADED',
  LIKESHOP: 'LIKESHOP',
  // Types of galleries
  CAMPAIGNS: 'CAMPAIGNS',
  LIBRARY: 'LIBRARY',
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_STORIES: 'INSTAGRAM_STORIES',
  INSTAGRAM_UGC: 'INSTAGRAM_UGC',
  INSTAGRAM_STORY_NARRATIVE: 'INSTAGRAM_STORY_NARRATIVE',
  INSTAGRAM_STORY_FRAME: 'INSTAGRAM_STORY_FRAME',
  INSTAGRAM_LIKESHOP: 'INSTAGRAM_LIKESHOP',
  INSTAGRAM_OTHER: 'INSTAGRAM_OTHER',
  TIKTOK_LIKESHOP: 'TIKTOK_LIKESHOP',
  PINTEREST: 'PINTEREST',
  FACEBOOK: 'FACEBOOK',
  FACEBOOK_TOP_POSTS: 'FACEBOOK_TOP_POSTS',
  FACEBOOK_COMPETITIVE: 'FACEBOOK_COMPETITIVE',
  YOUTUBE: 'YOUTUBE',
  COMPETITIVE_INSTAGRAM: 'COMPETITIVE_INSTAGRAM',
  COMPETITIVE_TWITTER: 'COMPETITIVE_TWITTER',
  COMPETITIVE_YOUTUBE: 'COMPETITIVE_YOUTUBE',
  CONTENT_TAGS: 'CONTENT_TAGS',
  // Images
  THUMBNAIL_SIZE: 640,
  // Types of content
  TIKTOK: 'TIKTOK',
  TWITTER: 'TWITTER',
  TWITTER_LINK: 'TWITTER_LINK',
  TWITTER_TEXT: 'TWITTER_TEXT',
  FACEBOOK_TEXT: 'FACEBOOK_TEXT',
  FACEBOOK_LINK: 'FACEBOOK_LINK',
  FACEBOOK_TEXT_LINK: 'FACEBOOK_TEXT_LINK',
  UPLOAD: 'UPLOAD',
  // Doubles as a board type (compared against backend gallery_type) and a media source.
  AI_GENERATION: 'AI_GENERATION',
  LINKEDIN: 'LINKEDIN',
  THREADS: 'THREADS',
  BLUESKY: 'BLUESKY',
  SNAPCHAT: 'SNAPCHAT',
};

export const socialPlatformConnectionProviders = [
  'instagram',
  'twitter',
  'facebook',
  'facebook_ads',
  'pinterest',
  'tiktok',
];

export const galleryTypes = constants;

export const richPinValidatorUrl = 'https://developers.pinterest.com/tools/url-debugger/';

export const termsOfServiceUrl = 'https://dashsocial.com/terms';

export const privacyPolicyUrl = 'https://dashsocial.com/privacy';

export const influencerFaqUrl = 'https://help.dashsocial.com/hc/en-us/articles/31970845358989';

export const RESOURCE_CENTER_BASE_URL = 'https://help.dashsocial.com';

const tweetNumberingLength = 8;
// Limit in chars as _raison d'etre_ of Twitter
export const maxTweetLength = 280;

// Buffer of 8 chars for numbering in format `(xx/xx) `
export const maxTweetLengthWithAutoNumbering = maxTweetLength - tweetNumberingLength;

export const maxLongFormTweetLength = 25000;

export const maxLongFormTweetLengthWithAutoNumbering =
  maxLongFormTweetLength - tweetNumberingLength;

// Limit from https://developers.facebook.com/docs/instagram-api/reference/ig-user/media
export const maxInstagramCaptionLength = 2200;

// Limit set to match `maxInstagramCaptionLength`; seems to be real IG limit
export const maxInstagramCommentLength = 2200;

// Limit is undocumented, but proven in testing that comments surpassing 8000 fail to publish
export const maxFacebookCommentLength = 8000;

// Comments with >30 hashtags will publish as inactive. This is a product decision to limit users to 30 at time of writing this.
export const maxFacebookHashtags = 30;

// Limit from https://bytedance.us.feishu.cn/docs/doccnq3SeHrZkncd0AdxL69MCEe#DMh0RO
// (TikTok Business Accounts API)
export const maxTiktokAutoPublishCaptionLength = 2200;

// Was able to publish caption of 500 chars manually on TikTok
export const maxTiktokSendNotificationCaptionLength = 2200;

// Limit from TikTok API for photo post titles
export const maxTiktokTitleLength = 90;

// Limit from https://docs.bsky.app/docs/advanced-guides/intent-links#compose-post
export const maxBlueskyCaptionLength = 300;

// Limit from https://developers.facebook.com/docs/threads/posts#threads-posts
export const maxThreadsCaptionLength = 500;

// Limit from https://developers.facebook.com/docs/instagram-api/reference/ig-user/media
export const maxInstagramHashtags = 30;

// Limit from https://learn.microsoft.com/en-us/linkedin/compliance/integrations/shares/ugc-post-api?tabs=http
export const maxLinkedInCaptionLength = 3000;

export const debounceInputDelay = 1000;

export const debounceInputDelayShort = 500;

export const debounceSearch = 250;

export const autoCompleteDebounceDelay = 300;

export const hashtagValidationDebounceDelay = 200;

export const urlPrefix = 'https://';

export const pinterestUrl = 'https://pinterest.com';

// extra time in minutes to wait before showing the overdue label
export const overdueDelay = 20;

export const overdueDraftDelay = 1;

// Interval for tracking Mixpanel sessions
export const sessionInterval = 30 * 60000;

export const trackSessionKey = 'dh.lastsessiontime';

export const imageCropperPresets = {
  square: {
    name: 'Square',
    ratio: '1:1',
    ratioNumericValue: 1,
  },
  instagramPortrait: {
    name: 'Instagram Portrait',
    ratio: '4:5',
    ratioNumericValue: 4 / 5,
  },
  instagramLandscape: {
    name: 'Instagram Landscape',
    ratio: '1.91:1',
    ratioNumericValue: 1.91,
  },
  instagramStory: {
    name: 'Instagram Story',
    ratio: '9:16',
    ratioNumericValue: 9 / 16,
  },
  pinterestPin: {
    name: 'Pinterest Pin',
    ratio: '2:3',
    ratioNumericValue: 2 / 3,
  },
  tweet: {
    name: 'X Post',
    ratio: '1.77:1',
    ratioNumericValue: 1.77,
  },
  facebookPost: {
    name: 'Facebook Post',
    ratio: '1.91:1',
    ratioNumericValue: 1.91,
  },
  likeshopBanner: {
    name: 'LikeShop Banner',
    ratio: '2:1',
    ratioNumericValue: 2,
  },
  tiktokLikeShop: {
    name: 'LikeShop Pinned Content',
    ratio: '3:4',
    ratioNumericValue: 3 / 4,
  },
};

export const videoCropperPresets = {
  square: {
    name: 'Square',
    ratio: '1:1',
    ratioNumericValue: 1,
  },
  instagramPortrait: {
    name: 'Instagram Portrait',
    ratio: '4:5',
    ratioNumericValue: 4 / 5,
  },
  instagramLandscape: {
    name: 'Instagram Landscape',
    ratio: '16:9',
    ratioNumericValue: 16 / 9,
  },
  instagramStory: {
    name: 'Instagram Story',
    ratio: '9:16',
    ratioNumericValue: 9 / 16,
  },
  pinterestPin: {
    name: 'Pinterest Pin',
    ratio: '2:3',
    ratioNumericValue: 2 / 3,
  },
  tweet: {
    name: 'Post',
    ratio: '1.77:1',
    ratioNumericValue: 1.77,
  },
  facebookPortrait: {
    name: 'Facebook Portrait',
    ratio: '9:16',
    ratioNumericValue: 9 / 16,
  },
  facebookLandscape: {
    name: 'Facebook Landscape',
    ratio: '16:9',
    ratioNumericValue: 16 / 9,
  },
  tiktokVideo: {
    name: 'TikTok Video',
    ratio: '9:16',
    ratioNumericValue: 9 / 16,
  },
  tiktokLikeShop: {
    name: 'LikeShop Pinned Content',
    ratio: '3:4',
    ratioNumericValue: 3 / 4,
  },
};

const instagramFeedPostCropperBase = {
  instagramPortrait: {
    name: 'Portrait',
    ratio: '4:5',
  },
  square: {
    name: 'Square',
    ratio: '1:1',
  },
};

export const instagramFeedPostCropperPresetsV2 = {
  image: {
    ...instagramFeedPostCropperBase,
    instagramLandscape: {
      name: 'Landscape',
      ratio: '1.91:1',
    },
    customPortrait: {
      name: 'Portrait',
      ratio: '3:4',
    },
    tallPortrait: {
      name: 'Portrait',
      ratio: '5:7',
    },
  },
  video: {
    ...instagramFeedPostCropperBase,
    instagramLandscape: {
      name: 'Landscape',
      ratio: '16:9',
    },
    customPortrait: {
      name: 'Portrait',
      ratio: '3:4',
    },
    tallPortrait: {
      name: 'Portrait',
      ratio: '5:7',
    },
  },
};

export const instagramStoryCropperPresetsV2 = {
  instagramStory: {
    name: 'Story / Full Screen Video',
    ratio: '9:16',
  },
};

export const instagramStoryReelCropperPresets = {
  instagramStory: {
    name: 'Story / Reel',
    ratio: '9:16',
  },
};

export const facebookReelCropperPresets = {
  facebookPortrait: {
    name: 'Reel',
    ratio: '9:16',
  },
};

export const instagramReelCoverImageCropperPresets = {
  instagramStory: {
    name: 'Story / Reel',
    ratio: '9:16',
  },
  instagramPortrait: {
    name: 'Portrait',
    ratio: '4:5',
  },
};

export const facebookReelCoverImageCropperPresets = {
  facebookPortrait: {
    name: 'Reel',
    ratio: '9:16',
  },
};

export const tikTokCropperPresets = {
  tiktokVideo: {
    name: 'TikTok Video',
    ratio: '9:16',
  },
};

export const tikTokImageCropperPresets = {
  tiktokPortrait: {
    name: 'TikTok Portrait',
    ratio: '9:16',
  },
  tiktokSquare: {
    name: 'TikTok Square',
    ratio: '1:1',
  },
  tiktokLandscape: {
    name: 'TikTok Landscape',
    ratio: '16:9',
  },
};

export const youtubeCropperPresets = {
  youtubeShort: {
    name: 'YouTube Short',
    ratio: '9:16',
    ratioNumericValue: 9 / 16,
  },
};

export const snapchatCropperPresets = {
  snapchatSnap: {
    name: 'Snapchat Snap',
    ratio: '9:16',
    ratioNumericValue: 9 / 16,
  },
};

export const sourceEnum = {
  EDITOR: 'EDITOR',
  FACEBOOK: 'FACEBOOK',
  FACEBOOK_ADS: 'FACEBOOK_ADS',
  FACEBOOK_LINK: 'FACEBOOK_LINK',
  FACEBOOK_TEXT: 'FACEBOOK_TEXT',
  FACEBOOK_TEXT_LINK: 'FACEBOOK_TEXT_LINK',
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_STORY_UGC: 'INSTAGRAM_STORY_UGC',
  LINKEDIN: 'LINKEDIN',
  PINTEREST: 'PINTEREST',
  TIKTOK: 'TIKTOK',
  TWITTER: 'TWITTER',
  TWITTER_LINK: 'TWITTER_LINK',
  TWITTER_TEXT: 'TWITTER_TEXT',
  UPLOAD: 'UPLOAD',
  YOUTUBE: 'YOUTUBE',
  AI_GENERATION: 'AI_GENERATION',
};
export const typeEnum = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  CAROUSEL: 'CAROUSEL',
  REEL: 'REEL',
  MULTI_IMAGE: 'MULTI_IMAGE',
};

export const adsUserPlatforms = ['ads', 'library'];

export const regexExpressions = {
  s3_domain: /^https:\/\/dashhudson-(static|dev)\.s3(-accelerate)?\.amazonaws\.com/,
  cdn_domain: /^https:\/\/cdn\.(dashhudson|dashsocial|dhdev)\.(com|io)/,
  instagram_handle: /^([A-Za-z0-9_](?:(?:[A-Za-z0-9_]|(?:\.(?!\.))){0,28}(?:[A-Za-z0-9_]))?)$/,

  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
};

export const FILTERS_OPERATORS = {
  IS: 'is',
  CONTAIN: 'contain',
  NOT_CONTAIN: 'not_contain',
  GREATER_THAN: 'gt',
  LESS_THAN: 'lt',
  BETWEEN: 'between',
  NOT_BETWEEN: 'not_between',
  IN: 'in',
};

export const LOGICAL_OPERATORS = {
  AND: 'AND',
  OR: 'OR',
};

export const FILTERS_OPERATORS_LABEL = {
  is: 'is',
  contain: 'contains',
  not_contain: 'does not contain',
  gt: 'is greater than',
  lt: 'is less than',
  between: 'is between',
  not_between: 'is not between',
  in: 'in',
};

export const PREDICTION_MODEL = {
  ENGAGEMENT: {
    label: 'engagement',
    field: 'eng',
    v2field: 'engagement',
    icon: 'pieGraph',
  },
};

export const UPLOAD_STATUS = {
  SUCCESS: 'success',
  PROCESSING: 'processing',
  PENDING: 'pending',
  FAILED: 'failed',
  INIT: 'init',
  UPLOADING: 'uploading',
};

export const PREDICTION_RATINGS = {
  bad: {
    color: '#B1C1D2',
    tooltip: 'This content is likely to perform lower than average',
  },
  good: {
    color: colours.SUCCESS.SUCCESS_500,
    tooltip: 'This content is likely to perform better than average',
  },
  excellent: {
    color: '#F5A623',
    tooltip: 'This content is likely a top performer',
  },
};

export const SKELETON_TYPES = {
  chart: 'chart',
  title: 'title',
  topLineStats: 'topLineStats',
  topPosts: 'topPosts',
  circle: 'circle',
  comment: 'comment',
  competitorOverviewBarChartSkeleton: 'competitorOverviewBarChartSkeleton',
  longTitle: 'longTitle',
  horizontalCard: 'horizontalCard',
  productCard: 'productCard',
  mediaCard: 'mediaCard',
  blankMediaCard: 'blankMediaCard',
  ugcMediaCard: 'ugcMediaCard',
  notification: 'notification',
  galleryCard: 'galleryCard',
  galleryCardSquareMedia: 'galleryCardSquareMedia',
  picture: 'picture',
  mediaTileMatrix: 'mediaTileMatrix',
  list: 'list',
  inboxMessage: 'inboxMessage',
  inboxMessageDetail: 'inboxMessageDetail',
  directMessage: 'directMessage',
  instagramStoryCard: 'instagramStoryCard',
  wideMediaTile: 'wideMediaTile',
  verticalMediaTile: 'verticalMediaTile',
  presetReplyPopup: 'presetReplyPopup',
  campaignCard: 'campaignCard',
  youtubeMediaCard: 'youtubeMediaCard',
  TikTokTrendingSoundsSkeleton: 'TikTokTrendingSoundsSkeleton',
  trendGroup: 'trendGroup',
  monthlyDateContents: 'monthlyDateContents',
  weeklyDateContents: 'weeklyDateContents',
  card: 'card',
  accountSelector: 'accountSelector',
  horizontalDistributionChartSkeleton: 'horizontalDistributionChartSkeleton',
  horizontalDistributionFlexChartSkeleton: 'horizontalDistributionFlexChartSkeleton',
  influencerTile: 'influencerTile',
  userDropdownSelect: 'userDropdownSelect',
  interactionCard: 'interactionCard',
  tag: 'tag',
  adsAccountsPopupSkeleton: 'adsAccountsPopupSkeleton',
  chatBubbleSkeleton: 'chatBubbleSkeleton',
  chatFeedSkeleton: 'chatFeedSkeleton',
  tileMatrix: 'tileMatrix',
};

export const VISION_AI_CHAT_BUBBLE_SKELETON_TYPES = {
  USER_BUBBLE: 'USER_BUBBLE',
  SYSTEM_BUBBLE: 'SYSTEM_BUBBLE',
};

export const PLATFORM_POST_TYPES = {
  FACEBOOK: 'Post',
  TWITTER: 'Post',
  PINTEREST: 'Pin',
  INSTAGRAM: 'Post',
};

export const DOWNLOAD_TYPES = {
  PDF: 'PDF',
  CSV: 'CSV',
};

// Issue logged here: https://developers.facebook.com/support/bugs/426770815432371/
export const SKEWED_IG_STORY_REACH_MESSAGE =
  "Due to an ongoing issue with Facebook's API, some stories may show higher reach than impressions.";

export const REPORT_MEDIA_LIMIT = 100;

export const SOURCE_TO_METRIC_PROP = {
  FACEBOOK_LINK: constants.FACEBOOK.toLowerCase(),
  FACEBOOK_TEXT: constants.FACEBOOK.toLowerCase(),
  FACEBOOK_TEXT_LINK: constants.FACEBOOK.toLowerCase(),
  FACEBOOK_OTHER: constants.FACEBOOK_COMPETITIVE.toLowerCase(),
  FACEBOOK_OTHER_TEXT: constants.FACEBOOK_COMPETITIVE.toLowerCase(),
  FACEBOOK_OTHER_LINK: constants.FACEBOOK_COMPETITIVE.toLowerCase(),
  FACEBOOK_OTHER_TL: constants.FACEBOOK_COMPETITIVE.toLowerCase(),
  TWITTER_LINK: constants.TWITTER.toLowerCase(),
  TWITTER_TEXT: constants.TWITTER.toLowerCase(),
  INSTAGRAM_STORY: 'instagram_story_frame',
};

export const MEDIA_CARD_FORMAT = Object.freeze({
  NUMBER: {
    formatType: 'number',
  },
  TWO_DECIMAL: {
    formatType: 'number',
    formatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  PERCENTAGE: {
    formatType: 'percent',
    formatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  DURATION: {
    formatType: 'duration',
  },
  DURATION_MILLISECONDS: {
    formatType: 'duration',
    formatOptions: {
      durationOptions: {
        rawUnit: 'millisecond',
      },
    },
  },
});

const instagramMediaCardDefaults = {
  labels: {
    comments_count: 'Comments',
    engagement: 'Engagement Rate',
    like_count: 'Likes',
    saved: 'Saves',
    totalEngagement: 'Engagements',
    reach: 'Estimated Reach',
    ugcEffectiveness: 'Effectiveness',
  },
  metrics: ['engagement', 'effectiveness', 'totalEngagement', 'reach'],
};

const instagramStoryMediaCardDefaults = {
  labels: {
    igStoryAvgReach: 'Avg. Reach',
    igStoryAvgImpressions: 'Avg. Impressions (Discontinued)',
    igStoryAvgCompletionRate: 'Avg. Completion Rate (Discontinued)',
    igStoryAvgExitRate: 'Avg. Exit Rate (Discontinued)',
    igStorySwipeUps: 'Stories Link Clicks',
    igStoryAvgViews: 'Avg. Views',
    igStoryAvgCompletionRateViews: 'Avg. Completion Rate',
    igStoryAvgExitRateViews: 'Avg. Exit Rate',
  },
  metrics: [
    'igStoryAvgReach',
    'igStoryAvgViews',
    'igStoryAvgCompletionRateViews',
    'igStoryAvgExitRateViews',
  ],
};

const instagramCompetitiveMediaCardDefaults = {
  labels: {
    commentsCount: 'Comments',
    engagement: 'Engagement Rate',
    likeCount: 'Likes',
    reach: 'Estimated Reach',
  },
  metrics: ['commentsCount', 'engagement', 'reach', 'likeCount'],
};

const instagramStoryFrameUgcMediaCardDefaults = {
  labels: {
    views: 'Impressions',
    reach: 'Reach',
    completion_rate: 'Completion Rate',
    exit_rate: 'Exit Rate',
    exits: 'Exits',
  },
};

export const instagramGalleryCardDefaults = {
  metrics: [
    'instagramAvgReach',
    'instagramTotalLikes',
    'instagramTotalComments',
    'instagramAvgEngagement',
    'instagramAvgEffectivenessEngagements',
  ],
  labels: {
    instagramAvgReach: 'Avg. Reach - Organic',
    instagramTotalLikes: 'Likes',
    instagramTotalComments: 'Comments',
    instagramAvgEngagement: 'Avg. Engagement Rate - Organic (F)',
    instagramAvgEffectivenessEngagements: 'Avg. Effectiveness - Organic',
  },
};

export const competitiveGalleryCardDefaults = { ...instagramGalleryCardDefaults };

export const instagramMetricFormats = {
  instagramAvgReach: 'number_with_commas',
  instagramTotalLikes: 'number_with_commas',
  instagramTotalComments: 'number_with_commas',
  instagramAvgEngagement: 'percent',
  instagramAvgEffectiveness: 'percent',
  instagramAvgEffectivenessEngagements: 'percent',
};

export const snapchatMediaCardDefaults = {
  metrics: ['views', 'viewers', 'avgViewTimeSeconds', 'engagements'],
  labels: {
    views: 'Views',
    viewers: 'Viewers',
    avgViewTimeSeconds: 'Avg. View Time',
    engagements: 'Engagements',
  },
};

export const DEFAULT_METRICS = {
  instagram: instagramMediaCardDefaults.metrics,
  instagram_stories: instagramStoryMediaCardDefaults.metrics,
  instagram_story_frame: instagramStoryMediaCardDefaults.metrics,
  instagram_competitive: instagramCompetitiveMediaCardDefaults.metrics,
  facebook: facebookMediaCardDefaults.metrics,
  facebook_competitive: facebookCompetitiveMediaCardDefaults.metrics,
  pinterest: pinterestMediaCardDefaults.metrics,
  twitter: twitterMediaCardDefaults.metrics,
  tiktok: tiktokMediaCardDefaults.metrics,
  youtube: youtubeMediaCardDefaults.metrics,
  snapchat: snapchatMediaCardDefaults.metrics,
};

export const DEFAULT_METRIC_LABELS = {
  instagram: instagramMediaCardDefaults.labels,
  instagram_stories: instagramStoryMediaCardDefaults.labels,
  instagram_competitive: instagramCompetitiveMediaCardDefaults.labels,
  instagram_story_frame: instagramStoryFrameUgcMediaCardDefaults.labels,
  facebook: facebookMediaCardDefaults.labels,
  facebook_competitive: facebookCompetitiveMediaCardDefaults.labels,
  pinterest: pinterestMediaCardDefaults.labels,
  twitter: twitterMediaCardDefaults.labels,
  youtube: youtubeMediaCardDefaults.labels,
  snapchat: snapchatMediaCardDefaults.labels,
};

export const DEFAULT_BOARD_METRICS = {
  TIKTOK: tiktokGalleryCardDefaults.metrics,
  FACEBOOK: facebookGalleryCardDefaults.metrics,
  TWITTER: twitterGalleryCardDefaults.metrics,
  YOUTUBE: youtubeGalleryCardDefaults.metrics,
  PINTEREST: pinterestGalleryCardDefaults.metrics,
  INSTAGRAM: instagramGalleryCardDefaults.metrics,
  COMPETITIVE: competitiveGalleryCardDefaults.metrics,
};

export const DEFAULT_BOARD_METRIC_LABELS = {
  TIKTOK: tiktokGalleryCardDefaults.labels,
  FACEBOOK: facebookGalleryCardDefaults.labels,
  TWITTER: twitterGalleryCardDefaults.labels,
  YOUTUBE: youtubeGalleryCardDefaults.labels,
  PINTEREST: pinterestGalleryCardDefaults.labels,
  INSTAGRAM: instagramGalleryCardDefaults.labels,
};

export const BOARD_METRIC_LABEL_FORMATS = {
  TIKTOK: tiktokMetricFormats,
  FACEBOOK: facebookMetricFormats,
  TWITTER: twitterMetricFormats,
  PINTEREST: pinterestMetricFormats,
  YOUTUBE: youtubeMetricFormats,
  INSTAGRAM: instagramMetricFormats,
};

export const BOARD_CONTENT_NAMES = {
  TIKTOK: 'Video',
  TWITTER: 'Post',
  YOUTUBE: 'Video',
};

export const BOARD_ROUTES = {
  TIKTOK: 'tiktok.galleries',
  FACEBOOK: 'facebook.galleries',
  TWITTER: 'twitter.galleries',
  INSTAGRAM_STORY: 'instagram.yourStories.storyBoards',
  PINTEREST: 'pinterest.galleries',
  YOUTUBE: 'youtube.galleries',
  LINKEDIN: 'linkedin.galleries',
  THREADS: 'threads.galleries',
  SNAPCHAT: 'snapchat.galleries',
  COMPETITIVE_INSTAGRAM: 'competitors.instagram.galleries',
  COMPETITIVE_TWITTER: 'competitors.twitter.galleries',
  COMPETITIVE_YOUTUBE: 'competitors.youtube.galleries',
};

export const BOARD_DETAIL_ROUTES = {
  TIKTOK: 'tiktok.gallery.detail',
  FACEBOOK: 'facebook.gallery.detail',
  TWITTER: 'twitter.gallery.detail',
  INSTAGRAM: 'instagram.galleries.board',
  INSTAGRAM_STORY: 'instagram.stories.galleries.gallery',
  PINTEREST: 'pinterest.galleries.detail',
  YOUTUBE: 'youtube.gallery.detail',
  LINKEDIN: 'linkedin.galleries.detail',
  THREADS: 'threads.galleries.detail',
  SNAPCHAT: 'snapchat.galleries.detail',
  COMPETITIVE_INSTAGRAM: 'competitors.instagram.galleries.board',
  COMPETITIVE_TWITTER: 'competitors.twitter.galleries.board',
  COMPETITIVE_YOUTUBE: 'competitors.youtube.galleries.board',
};

export const GALLERY_CARD_DISPLAY_FORMAT = {
  DEFAULT_THUMBNAIL: 'defaultThumbnail',
  HORIZONTAL_THUMBNAIL: 'horizontalThumbnail',
  VERTICAL_THUMBNAIL: 'verticalThumbnail',
};

export const commentAndNotificationTypes = {
  COMMENT: 'COMMENT',
  COMMENT_REPLY: 'COMMENT_REPLY',
  COMMENT_MENTION: 'COMMENT_MENTION',
};

export const notificationDescriptionsByType = {
  COMMENT_MENTION: 'mentioned you in a comment',
  COMMENT_REPLY: 'replied to your comment',
  COMMENT: 'commented',
};

export const COMPOSER_TYPES = {
  comment: {
    label: 'comment',
    actionText: 'Comment',
    placeHolderText: 'Add a comment or @mention a teammate',
  },
  reply: {
    label: 'reply',
    actionText: 'Reply',
    placeHolderText: 'Reply',
  },
  edit: {
    label: 'edit',
    actionText: 'Save',
    placeHolderText: 'Edit',
  },
};

export const COMMENT_RESOURCE_TYPES = {
  SCHEDULER_INSTAGRAM_POST: 'SCHEDULER_INSTAGRAM_POST',
  SCHEDULER_PINTEREST_POST: 'SCHEDULER_PINTEREST_POST',
  SCHEDULER_FACEBOOK_POST: 'SCHEDULER_FACEBOOK_POST',
  SCHEDULER_TWITTER_POST: 'SCHEDULER_TWITTER_POST',
  LIBRARY_BRAND_MEDIA: 'LIBRARY_BRAND_MEDIA',
  COMMUNITY_INTERNAL_NOTE: 'COMMUNITY_INTERNAL_NOTE',
  COMMUNITY_ASSIGNMENT: 'COMMUNITY_ASSIGNMENT',
  COMMUNITY_RULE_EXECUTED: 'COMMUNITY_RULE_EXECUTED',
  COMMUNITY_RULE_DELETE_FAILED: 'COMMUNITY_RULE_DELETE_FAILED',
  COMMUNITY_RULE_HIDE_FAILED: 'COMMUNITY_RULE_HIDE_FAILED',
  COMMUNITY_RULE_SEND_DM_FAILED: 'COMMUNITY_RULE_SEND_DM_FAILED',
};

export const INSTAGRAM_STORY_CARD_CONSTANTS = {
  MIN_FRAMES_CONTAINER_WIDTH: 200,
  DEFAULT_NUMBER_OF_FRAMES: 3,
  SMALL_NUMBER_OF_FRAMES: 2,
};

export const ENTERTAINING_AVG_LINE_METRICS = {
  AVG_ORGANIC_VIDEO_VIEWS: 'AVG_ORGANIC_VIDEO_VIEWS',
  AVG_VIDEO_VIEWS: 'AVG_VIDEO_VIEWS',
  AVG_VIEWS_VIDEO_REEL: 'AVG_VIEWS_VIDEO_REEL',
  AVG_ENTERTAINMENT_SCORE_REEL: 'AVG_ENTERTAINMENT_SCORE_REEL',
  AVG_ENTERTAINMENT_SCORE: 'AVG_ENTERTAINMENT_SCORE',
};

export const OVERVIEW_ENTERTAINING_AVG_LINE_METRIC = {
  '-INSTAGRAM_ENTERTAINMENT_SCORE': {
    metric: ENTERTAINING_AVG_LINE_METRICS.AVG_ENTERTAINMENT_SCORE_REEL,
    channel: 'INSTAGRAM',
    post_types: ['reel'],
    attribute: {
      value: 'entertainmentScore',
      isDecimal: true,
    },
    icon: 'leisurePartyPopper',
    tooltips: toolTips.mostEntertaining.entertainmentScore,
  },
  '-INSTAGRAM_VIDEO_VIEWS': {
    metric: ENTERTAINING_AVG_LINE_METRICS.AVG_ORGANIC_VIDEO_VIEWS,
    channel: 'INSTAGRAM',
    post_types: ['video', 'reel'],
    attribute: {
      value: 'videoViews',
      isDecimal: false,
    },
    icon: 'controlPlay',
    tooltips: toolTips.mostEntertaining.videoViews,
  },
  '-INSTAGRAM_VIEWS': {
    metric: ENTERTAINING_AVG_LINE_METRICS.AVG_VIEWS_VIDEO_REEL,
    channel: 'INSTAGRAM',
    post_types: ['video', 'reel'],
    attribute: {
      value: 'views',
      isDecimal: false,
    },
    icon: 'controlPlay',
    tooltips: toolTips.mostEntertaining.views,
  },
  '-TIKTOK_ENTERTAINMENT_SCORE': {
    metric: ENTERTAINING_AVG_LINE_METRICS.AVG_ENTERTAINMENT_SCORE,
    channel: 'TIKTOK',
    attribute: {
      value: 'entertainmentScore',
      isDecimal: true,
    },
    icon: 'leisurePartyPopper',
    tooltips: toolTips.mostEntertaining.entertainmentScore,
  },
  '-TIKTOK_VIEWS': {
    metric: ENTERTAINING_AVG_LINE_METRICS.AVG_VIDEO_VIEWS,
    channel: 'TIKTOK',
    attribute: {
      value: 'views',
      isDecimal: false,
    },
    icon: 'controlPlay',
    tooltips: toolTips.mostEntertaining.videoViews,
  },
};

export const mostEntertainingVideoLimit = 7;
export const mostEntertainingMinAbbrNumber = 1000000;

export const SEARCH_BAR_PLACEHOLER = 'Search Media';
export const COMPETITIVE_SEARCH_BAR_PLACEHOLER = 'Filter by keywords(s)';

export const maxTopPinsStartDateDays = 91;

export const MOST_ENTERTAINING_MIXPANEL_FILTER_EVENT_NAME = {
  INSTAGRAM: 'Instagram Overview Content Insights',
  TIKTOK: 'TikTok Content Selection',
};

export const CHART_COLORS = [
  '#7ED321',
  '#7597F0',
  '#C9E321',
  '#39BCE6',
  '#EB4D4D',
  '#69DCDD',
  '#F6A623',
  '#8943FF',
  '#F0649A',
  '#51CC92',
  '#C5994C',
  '#4BA1D0',
  '#E179DA',
  '#C26C47',
  '#ED7171',
  '#64DB5B',
  '#F6D423',
  '#4356FF',
  '#BD7C9B',
  '#84AE63',
];

export const timeScaleTypes = {
  DAILY: {
    value: 'DAILY',
    label: 'By Day',
    unit: 'day',
    dateFormat: 'YYYY-MM-DD',
  },
  MONTHLY: {
    value: 'MONTHLY',
    label: 'By Month',
    unit: 'month',
    dateFormat: 'YYYY-MM',
  },
};

export const sortOrderOptions = {
  ASC: {
    value: 'ASC',
    label: 'Lowest to Highest',
  },
  DESC: {
    value: 'DESC',
    label: 'Highest to Lowest',
  },
};

export const mixpanelFilterOptions = {
  SCALE: 'Scale',
  GRAPH_METRIC: 'Graph Metric',
  DATE: 'Date',
  SORT_BY: 'Sort By',
  SORT_ORDER: 'Sort Order',
};

export const DROPDOWN_LIST_ITEMS = {
  YOUR_POSTS_CSV: {
    text: 'Your Posts CSV',
    action: 'downloadYourPostsCSV',
  },
};

export const SALES_TERRITORY_TO_INDUSTRY = {
  Beauty: 'Beauty',
  Fashion: 'Fashion',
  Publishing: 'Publishing',
  Retail: 'Retail',
  Media: 'Media',
  Automotive: 'Automotive',
  'Consumer Electronics': 'Consumer Electronics',
  'Consumer Goods': 'Consumer Packaged Goods',
  'Financial Services': 'Financial Services',
  'Food & Bev': 'Food & Beverage',
  'Home Decor / Home Goods': 'Home',
  'Luxury Goods & Jewelry': 'Luxury',
  Healthcare: 'Wellness',
  Travel: 'Travel',
  Technology: 'All Industries',
  Agency: 'All Industries',
  Other: 'All Industries',
  'Non Profit': 'All Industries',
  null: 'All Industries',
  undefined: 'All Industries',
};

export const VISION_AI_SENDER = {
  USER: 'USER',
  ASSISTANT: 'ASSISTANT',
};

export const REPORTING_PERIOD_PRESET_MAP = {
  LAST_24_HOURS: 'Last 24 Hours',
  PREVIOUS_24_HOURS: 'Previous 24 Hours',
  LAST_7_DAYS: 'Last 7 Days',
  PREVIOUS_7_DAYS: 'Previous 7 Days',
  LAST_WEEK: 'Last Week',
  PREVIOUS_WEEK: 'Previous Week',
  LAST_4_WEEKS: 'Last 4 Weeks',
  PREVIOUS_4_WEEKS: 'Previous 4 Weeks',
  LAST_MONTH: 'Last Month',
  PREVIOUS_MONTH: 'Previous Month',
  WEEK_TO_DATE: 'Week to Date',
  PREVIOUS_WEEK_TO_DATE: 'Previous Week to Date',
  MONTH_TO_DATE: 'Month to Date',
  PREVIOUS_MONTH_TO_DATE: 'Previous Month to Date',
  QUARTER_TO_DATE: 'Quarter to Date',
  PREVIOUS_QUARTER_TO_DATE: 'Previous Quarter to Date',
  YEAR_TO_DATE: 'Year to Date',
  YEAR_BEFORE: 'Year Before',
  ALL_TIME: 'All Time',
  CUSTOM: 'Custom',
};

export const branding = brandingDashSocial;
export const TWITTER_LONG_FORM_TEXT_ALLOWED_PLAN = Object.freeze({
  BASIC: 'Basic',
  PREMIUM: 'Premium',
  PREMIUM_PLUS: 'PremiumPlus',
});
